import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// ─────────────────────────────────────────────
// Pagination Component (unchanged)
// ─────────────────────────────────────────────
function Pagination({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange }) {
  const [inputPage, setInputPage] = useState(currentPage);

  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }

    range.forEach((i) => {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    });

    return rangeWithDots;
  };

  const handleInputChange = (e) => {
    const value = e.target.value === '' ? '' : parseInt(e.target.value);
    setInputPage(value);
  };

  const handleInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      const page = parseInt(inputPage);
      if (page >= 1 && page <= totalPages) {
        onPageChange(page);
      }
    }
  };

  const handleGoToPage = () => {
    const page = parseInt(inputPage);
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
      <div className="text-dark-gray text-sm">
        Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
      </div>

      <div className="flex items-center space-x-2">
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className={`px-3 py-1 rounded border ${
            currentPage === 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'hover:bg-gray-50 text-black'
          }`}
          title="First Page"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
            />
          </svg>
        </button>

        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-3 py-1 rounded border ${
            currentPage === 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'hover:bg-gray-50 text-black'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <div className="hidden sm:flex space-x-1">
          {getPageNumbers().map((page, index) =>
            page === '...' ? (
              <span key={`dots-${index}`} className="px-3 py-1 text-dark-gray">
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`px-3 py-1 rounded border min-w-[40px] ${
                  currentPage === page ? 'bg-black text-white' : 'hover:bg-gray-50 text-black'
                }`}
              >
                {page}
              </button>
            )
          )}
        </div>

        <span className="sm:hidden text-sm text-dark-gray">
          Page {currentPage} of {totalPages}
        </span>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-3 py-1 rounded border ${
            currentPage === totalPages
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'hover:bg-gray-50 text-black'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className={`px-3 py-1 rounded border ${
            currentPage === totalPages
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'hover:bg-gray-50 text-black'
          }`}
          title="Last Page"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 5l7 7-7 7M5 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      <div className="flex items-center space-x-3">
        <select
          value={itemsPerPage}
          onChange={(e) => onPageChange(1, parseInt(e.target.value))}
          className="border border-light-gray rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-black"
        >
          <option value={10}>10 / page</option>
          <option value={25}>25 / page</option>
          <option value={50}>50 / page</option>
          <option value={100}>100 / page</option>
        </select>

        <div className="flex items-center space-x-1">
          <span className="text-sm text-dark-gray">Go to</span>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={inputPage}
            onChange={handleInputChange}
            onKeyPress={handleInputKeyPress}
            className="w-16 border border-light-gray rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-black"
            onFocus={(e) => e.target.select()}
          />
          <button
            onClick={handleGoToPage}
            className="px-3 py-1 bg-black text-white text-sm rounded-lg hover:bg-gray-800"
          >
            Go
          </button>
        </div>
      </div>
    </div>
  );
}

function FinanceAndAccounting() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="p-6">
        <header className="flex justify-between items-center mb-3 bg-white p-3">
          <h1 className="text-black text-xl font-bold ml-2">Finance and Accounting</h1>
          <div className="flex space-x-6">
            <button
              onClick={() => navigate('/dashboard/core-modules/finance/billing-invoicing')}
              className="btn text-black font-bold"
            >
              Billing and Invoicing
            </button>
            <button
              onClick={() =>
                navigate('/dashboard/core-modules/finance/accounts-receivable-payable')
              }
              className="btn "
            >
              Accounts Receivable/Payable
            </button>
            <button
              onClick={() => navigate('/dashboard/core-modules/finance/financial-reporting')}
              className="btn "
            >
              Financial Reporting
            </button>
            <button
              onClick={() => navigate('/dashboard/core-modules/finance/tax-management')}
              className="btn "
            >
              Tax Management
            </button>
            <button
              onClick={() => navigate('/dashboard/core-modules/finance/audit-logs')}
              className="btn "
            >
              Audit Logs
            </button>
          </div>
        </header>

        <BillingInvoicing />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Billing & Invoicing Component
// ─────────────────────────────────────────────
function BillingInvoicing() {
  const navigate = useNavigate();
  const [allBookings, setAllBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [displayedBookings, setDisplayedBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [staffId] = useState(1);
  const [uniqueBillingTypes, setUniqueBillingTypes] = useState([]);
  const [showGSTModal, setShowGSTModal] = useState(false);
  const [gstNumber, setGstNumber] = useState('');
  const [gstErrors, setGstErrors] = useState('');
  const [updatingGST, setUpdatingGST] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  const [filters, setFilters] = useState({
    search: '',
    bookingStatus: '',
    paymentStatus: '',
    dateRange: {
      startDate: '',
      endDate: '',
    },
    checkoutDateFilter: '',
  });

  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceType, setInvoiceType] = useState('hotel');
  const [invoiceData, setInvoiceData] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [customEmail, setCustomEmail] = useState('');
  const [emailOption, setEmailOption] = useState('guest');
  const [emailError, setEmailError] = useState('');

  const [showBillingTypeModal, setShowBillingTypeModal] = useState(false);
  const [selectedBillingType, setSelectedBillingType] = useState('room_only');
  const [includeCharges, setIncludeCharges] = useState({
    room: true,
    food: false,
    laundry: false,
    amenities: false,
    other: false,
  });
  const [showDiscount, setShowDiscount] = useState(true);

  // Adjustment modal state
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [adjustments, setAdjustments] = useState([]);
  const [billingRecords, setBillingRecords] = useState([]);
  const [loadingBillingRecords, setLoadingBillingRecords] = useState(false);
  const [currentAdjustment, setCurrentAdjustment] = useState({
    amount: '',
    adjustment_type: 'flat',
    is_discount: true,
    billing_type: 'Manual Adjustment - Discount',
    base_category: '',
    reason: '',
    is_taxable: false,
    override_tax_amount: null,
    base_amount: null,
    billing_id: null,
  });

  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [filteredOrdersData, setFilteredOrdersData] = useState({
    orders: {},
    billings: [],
    uniqueOrderTypes: [],
  });
  const [uniqueOrderTypes, setUniqueOrderTypes] = useState([]);
  const [ordersData, setOrdersData] = useState({});

  // Per-food-invoice +/- used only by the Custom invoice modal
  const [foodInvoicesForBooking, setFoodInvoicesForBooking] = useState([]);
  const [loadingFoodInvoices, setLoadingFoodInvoices] = useState(false);
  const [excludedFoodInvoices, setExcludedFoodInvoices] = useState([]);

  const [dashboardOverview, setDashboardOverview] = useState({
    total_revenue: 0,
    total_collected: 0,
    total_refunded: 0,
    net_outstanding: 0,
    total_adjustments: 0,
    adjustment_charges: 0,
    adjustment_discounts: 0,
    total_vouchers: 0,
    paid_bookings: 0,
    pending_bookings: 0,
    today_checkins: 0,
    today_checkouts: 0,
  });

  const debouncedSearch = useCallback(
    debounce((value) => {
      handleFilterChange('search', value);
    }, 500),
    []
  );

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    applyFiltersAndPagination();
  }, [allBookings, filters, currentPage, itemsPerPage]);

  const fetchDashboardOverview = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/dashboard/overview?days=90`, {
        headers: { accept: 'application/json' },
      });

      if (!response.ok) throw new Error('Failed to fetch dashboard overview');

      const overviewData = await response.json();

      setDashboardOverview({
        total_revenue: parseFloat(overviewData.total_revenue) || 0,
        total_collected: parseFloat(overviewData.total_collected) || 0,
        total_refunded: parseFloat(overviewData.total_refunded) || 0,
        net_outstanding: parseFloat(overviewData.net_outstanding) || 0,
        total_adjustments: parseFloat(overviewData.total_adjustments) || 0,
        adjustment_charges: parseFloat(overviewData.adjustment_charges) || 0,
        adjustment_discounts: parseFloat(overviewData.adjustment_discounts) || 0,
        total_vouchers: parseFloat(overviewData.total_vouchers) || 0,
        paid_bookings: parseInt(overviewData.paid_bookings) || 0,
        pending_bookings: parseInt(overviewData.pending_bookings) || 0,
        today_checkins: parseInt(overviewData.today_checkins) || 0,
        today_checkouts: parseInt(overviewData.today_checkouts) || 0,
      });
    } catch (err) {
      console.error('Error fetching dashboard overview:', err);
      setDashboardOverview({
        total_revenue: 0,
        total_collected: 0,
        total_refunded: 0,
        net_outstanding: 0,
        total_adjustments: 0,
        adjustment_charges: 0,
        adjustment_discounts: 0,
        total_vouchers: 0,
        paid_bookings: 0,
        pending_bookings: 0,
        today_checkins: 0,
        today_checkouts: 0,
      });
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await fetchDashboardOverview();

      const bookingsResponse = await fetch(`${BQ_BASE_URL}/bq/api/dashboard/bookings?limit=100`, {
        headers: { accept: 'application/json' },
      });

      if (!bookingsResponse.ok) throw new Error('Failed to fetch bookings');

      const bookingsData = await bookingsResponse.json();

      if (!bookingsData || !bookingsData.bookings) {
        console.warn('No bookings data received');
        setAllBookings([]);
        setLoading(false);
        return;
      }

      const transformedBookings = bookingsData.bookings.map((bookingDetail) => {
        const guestDetails = bookingDetail.guest_details;
        const paymentMetrics = bookingDetail.payment_metrics;

        return {
          bookingid: guestDetails?.booking_id || '',
          orderid: guestDetails?.order_id || '',
          firstname: guestDetails?.guest_name?.split(' ')[0] || '',
          lastname: guestDetails?.guest_name?.split(' ').slice(1).join(' ') || '',
          emailid: guestDetails?.email || '',
          phonenumber: guestDetails?.phone || '',
          roomnumber: guestDetails?.room_number || '',
          roomtypename: guestDetails?.room_type || '',
          checkindate: guestDetails?.check_in || '',
          checkoutdate: guestDetails?.check_out || '',
          booking_status: guestDetails?.booking_status || '',
          booking_type: guestDetails?.booking_type || '',
          numberofguests: guestDetails?.number_of_guests || 1,
          lengthofstay: guestDetails?.length_of_stay || 0,
          reservationmadeon: guestDetails?.reservation_made_on || '',
          gst_number: guestDetails?.gst_number || '',
          billing: {
            total_billed: parseFloat(paymentMetrics?.bill_amount) || 0,
            total_paid: parseFloat(paymentMetrics?.paid_amount) || 0,
            net_payable: parseFloat(paymentMetrics?.balance) || 0,
            is_fully_paid_or_over: paymentMetrics?.status === 'PAID',
            total_adjustment_amount: parseFloat(paymentMetrics?.total_discounts) || 0,
            has_adjustments: (bookingDetail.adjustments?.length || 0) > 0,
          },
          adjustments: bookingDetail.adjustments || [],
          reversals: bookingDetail.reversals || [],
          refunds: bookingDetail.refunds || [],
          vouchers: bookingDetail.vouchers || [],
        };
      });

      setAllBookings(transformedBookings);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching data:', err);
      setAllBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndPagination = () => {
    const filtered = allBookings.filter((item) => {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch =
        filters.search === '' ||
        item.bookingid?.toLowerCase().includes(searchLower) ||
        item.orderid?.toLowerCase().includes(searchLower) ||
        item.firstname?.toLowerCase().includes(searchLower) ||
        item.lastname?.toLowerCase().includes(searchLower) ||
        item.emailid?.toLowerCase().includes(searchLower) ||
        item.roomnumber?.toLowerCase().includes(searchLower) ||
        item.roomtypename?.toLowerCase().includes(searchLower);

      const matchesBookingStatus =
        filters.bookingStatus === '' || item.booking_status === filters.bookingStatus;

      const matchesPaymentStatus =
        filters.paymentStatus === '' ||
        (filters.paymentStatus === 'paid' && item.billing?.is_fully_paid_or_over) ||
        (filters.paymentStatus === 'pending' &&
          !item.billing?.is_fully_paid_or_over &&
          item.billing?.total_billed > 0) ||
        (filters.paymentStatus === 'unpaid' && item.billing?.total_billed === 0);

      let matchesDateRange = true;
      if (filters.dateRange.startDate || filters.dateRange.endDate) {
        const bookingCheckin = new Date(item.checkindate);
        bookingCheckin.setHours(0, 0, 0, 0);

        if (filters.dateRange.startDate && filters.dateRange.endDate) {
          const startDate = new Date(filters.dateRange.startDate);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(filters.dateRange.endDate);
          endDate.setHours(23, 59, 59, 999);

          matchesDateRange = bookingCheckin >= startDate && bookingCheckin <= endDate;
        } else if (filters.dateRange.startDate) {
          const startDate = new Date(filters.dateRange.startDate);
          startDate.setHours(0, 0, 0, 0);
          matchesDateRange = bookingCheckin >= startDate;
        } else if (filters.dateRange.endDate) {
          const endDate = new Date(filters.dateRange.endDate);
          endDate.setHours(23, 59, 59, 999);
          matchesDateRange = bookingCheckin <= endDate;
        }
      }

      let matchesCheckoutDate = true;
      if (filters.checkoutDateFilter) {
        const checkoutDate = new Date(item.checkoutdate);
        checkoutDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        switch (filters.checkoutDateFilter) {
          case 'today':
            matchesCheckoutDate = checkoutDate.getTime() === today.getTime();
            break;
          case 'yesterday': {
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            matchesCheckoutDate = checkoutDate.getTime() === yesterday.getTime();
            break;
          }
          case 'this_week': {
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() - today.getDay());
            const endOfWeek = new Date(today);
            endOfWeek.setDate(today.getDate() + (6 - today.getDay()));
            matchesCheckoutDate = checkoutDate >= startOfWeek && checkoutDate <= endOfWeek;
            break;
          }
          case 'this_month': {
            const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            matchesCheckoutDate = checkoutDate >= startOfMonth && checkoutDate <= endOfMonth;
            break;
          }
          default:
            matchesCheckoutDate = true;
        }
      }

      return (
        matchesSearch &&
        matchesBookingStatus &&
        matchesPaymentStatus &&
        matchesDateRange &&
        matchesCheckoutDate
      );
    });

    setFilteredBookings(filtered);
    setTotalItems(filtered.length);

    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage);

    setDisplayedBookings(paginatedData);
  };

  const fetchBillingRecordsByOrder = async (orderId) => {
    setLoadingBillingRecords(true);
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/billing/by-order/${orderId}`, {
        headers: { accept: 'application/json' },
      });

      if (!response.ok) throw new Error('Failed to fetch billing records');

      const data = await response.json();
      setOrdersData(data.orders || {});
      setUniqueOrderTypes(data.unique_order_types || []);

      const filteredOrders = {};
      if (data.orders) {
        Object.keys(data.orders).forEach((orderType) => {
          if (orderType !== 'Manual Adjustment') {
            filteredOrders[orderType] = data.orders[orderType];
          }
        });
      }

      const filteredBillings =
        data.billings?.filter(
          (billing) => !billing.billing_type?.toLowerCase().includes('manual adjustment')
        ) || [];

      setBillingRecords(filteredBillings);

      const uniqueOrderTypesFiltered =
        data.unique_order_types?.filter((type) => type !== 'Manual Adjustment') || [];

      setFilteredOrdersData({
        orders: filteredOrders,
        billings: filteredBillings,
        uniqueOrderTypes: uniqueOrderTypesFiltered,
      });

      return {
        ...data,
        orders: filteredOrders,
        billings: filteredBillings,
        unique_order_types: uniqueOrderTypesFiltered,
      };
    } catch (err) {
      console.error('Error fetching billing records:', err);
      setEmailStatus({
        success: false,
        message: 'Failed to fetch billing records',
      });
      setTimeout(() => setEmailStatus(null), 3000);
      return null;
    } finally {
      setLoadingBillingRecords(false);
    }
  };

  const buildExcludedSuffix = () => {
    if (excludedFoodInvoices.length === 0) return '';
    return `&exclude_food_invoices=${encodeURIComponent(excludedFoodInvoices.join(','))}`;
  };

  const fetchFoodInvoicesForBooking = async (orderId) => {
    setLoadingFoodInvoices(true);
    setFoodInvoicesForBooking([]);
    try {
      const url = `${BQ_BASE_URL}/bq/api/invoice/data/${orderId}?format=folio`;
      const res = await fetch(url, { headers: { accept: 'application/json' } });
      if (!res.ok) throw new Error('Failed to fetch food invoices');
      const data = await res.json();
      setFoodInvoicesForBooking(data.food_invoices || []);
    } catch (err) {
      console.error('Error loading food invoices:', err);
      setFoodInvoicesForBooking([]);
    } finally {
      setLoadingFoodInvoices(false);
    }
  };

  const fetchInvoiceData = async (orderId, type = 'hotel') => {
    setLoadingInvoice(true);
    try {
      let url = `${BQ_BASE_URL}/bq/api/invoice/data/${orderId}?format=folio`;

      if (type === 'guest') {
        if (selectedBillingType === 'room_only') {
          url += `&room_rent=true&food_order=false&amenity_service=true&room_service=false&full_stay=false`;
        } else if (selectedBillingType === 'full_stay') {
          url += `&room_rent=true&food_order=true&amenity_service=true&room_service=true&full_stay=true`;
        } else if (selectedBillingType === 'custom') {
          url += `&room_rent=${includeCharges.room}`;
          url += `&food_order=${includeCharges.food}`;
          url += `&amenity_service=${includeCharges.amenities}`;
          url += `&room_service=${includeCharges.laundry || false}`;
          url += `&full_stay=false`;
        }
        url += buildExcludedSuffix();
      }

      const response = await fetch(url, { headers: { accept: 'application/json' } });
      if (!response.ok) throw new Error('Failed to fetch invoice data');

      const data = await response.json();

      if (type === 'guest') {
        setInvoiceData({
          ...data,
          adjustments: adjustments,
          billingType: selectedBillingType,
          note: getBillingTypeNote(selectedBillingType),
        });
      } else {
        setInvoiceData(data);
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
      alert('Failed to load invoice data');
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleSendEmailWithCustom = async (orderId, customEmailAddr) => {
    setEmailSending(true);
    setEmailStatus(null);

    try {
      let url = `${BQ_BASE_URL}/bq/api/invoice/email/${orderId}?recipient_email=${encodeURIComponent(
        customEmailAddr
      )}&format=folio`;

      if (invoiceType === 'guest') {
        if (selectedBillingType === 'room_only') {
          url += `&room_rent=true&food_order=false&amenity_service=true&room_service=false&full_stay=false`;
        } else if (selectedBillingType === 'full_stay') {
          url += `&room_rent=true&food_order=true&amenity_service=true&room_service=true&full_stay=true`;
        } else if (selectedBillingType === 'custom') {
          url += `&room_rent=${includeCharges.room}`;
          url += `&food_order=${includeCharges.food}`;
          url += `&amenity_service=${includeCharges.amenities}`;
          url += `&room_service=${includeCharges.laundry || false}`;
          url += `&full_stay=false`;
        }
        url += buildExcludedSuffix();
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: { accept: 'application/json' },
      });

      if (!response.ok) throw new Error('Failed to send email');

      const data = await response.json();
      setEmailStatus({
        success: true,
        message: data.message || `Invoice email sent successfully to ${customEmailAddr}`,
      });

      setTimeout(() => setEmailStatus(null), 3000);
      setShowEmailModal(false);
      setCustomEmail('');
    } catch (err) {
      console.error('Error sending email:', err);
      setEmailStatus({
        success: false,
        message: err.message || 'Failed to send invoice email',
      });
    } finally {
      setEmailSending(false);
    }
  };

  const getBillingTypeNote = (type) => {
    switch (type) {
      case 'room_only':
        return 'This invoice includes room charges only. Other services (food, laundry, etc.) are billed separately.';
      case 'full_stay':
        return 'This invoice includes all charges for your entire stay including room, food, and other services.';
      case 'custom':
        return 'This invoice includes selected charges as per your request.';
      default:
        return 'Thank you for staying with us.';
    }
  };

  const handleGenerateInvoice = (booking) => {
    setSelectedBooking(booking);
    setAdjustments([]);
    setExcludedFoodInvoices([]);
    setFoodInvoicesForBooking([]);
    setShowBillingTypeModal(true);
    fetchFoodInvoicesForBooking(booking.orderid);
  };

  const validateGST = (gst) => {
    if (!gst) return '';
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
    if (!gstRegex.test(gst)) {
      return 'Invalid GST number format (e.g., 22AAAAA0000A1Z5)';
    }
    return '';
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return 'Email is required';
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const handleGstChange = (e) => {
    const value = e.target.value.toUpperCase();
    setGstNumber(value);
    const error = validateGST(value);
    setGstErrors(error);
  };

  const handleBillingTypeSelect = async () => {
    setShowBillingTypeModal(false);
    setInvoiceType('guest');
    await fetchInvoiceData(selectedBooking.orderid, 'guest');
    setShowInvoiceModal(true);
  };

  const handleViewHotelInvoice = async (booking) => {
    setInvoiceType('hotel');
    setSelectedBooking(booking);
    setExcludedFoodInvoices([]);
    await fetchInvoiceData(booking.orderid, 'hotel');
    setShowInvoiceModal(true);
  };

  const handleOpenAdjustmentModal = async (booking) => {
    setSelectedBooking(booking);
    setCurrentAdjustment({
      amount: '',
      adjustment_type: 'flat',
      is_discount: true,
      billing_type: 'Manual Adjustment - Discount',
      base_category: '',
      reason: '',
      is_taxable: false,
      override_tax_amount: null,
      base_amount: null,
      billing_id: null,
    });

    await fetchBillingRecordsByOrder(booking.orderid);
    setShowAdjustmentModal(true);
  };

  const handleAddAdjustment = async () => {
    const amountValue = parseFloat(currentAdjustment.amount);

    if (
      !amountValue ||
      amountValue <= 0 ||
      !currentAdjustment.reason ||
      !currentAdjustment.base_category ||
      !selectedBooking
    ) {
      setEmailStatus({
        success: false,
        message: 'Please fill all required fields with valid values',
      });
      setTimeout(() => setEmailStatus(null), 3000);
      return;
    }

    if (
      currentAdjustment.adjustment_type === 'percentage' &&
      !currentAdjustment.billing_id &&
      !currentAdjustment.base_amount
    ) {
      setEmailStatus({
        success: false,
        message:
          'Please select a billing record or provide a base amount for percentage calculation',
      });
      setTimeout(() => setEmailStatus(null), 3000);
      return;
    }

    try {
      const adjustmentPayload = {
        bookingid: selectedBooking.bookingid,
        amount: amountValue,
        adjustment_type: currentAdjustment.adjustment_type,
        is_discount: currentAdjustment.is_discount,
        billing_type: currentAdjustment.billing_type,
        base_category: currentAdjustment.base_category,
        reason: currentAdjustment.reason,
        is_taxable: currentAdjustment.is_taxable,
        override_tax_amount: currentAdjustment.override_tax_amount,
        created_by: staffId,
      };

      if (currentAdjustment.billing_id) {
        adjustmentPayload.billing_id = currentAdjustment.billing_id;
      }

      if (currentAdjustment.adjustment_type === 'percentage' && currentAdjustment.base_amount) {
        adjustmentPayload.overrde_base_amount = parseFloat(currentAdjustment.base_amount);
      }

      const response = await fetch(`${BQ_BASE_URL}/bq/api/billing/manual-adjustment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(adjustmentPayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add adjustment');
      }

      const result = await response.json();

      const newAdjustment = {
        id: result.billing_id,
        amount: amountValue,
        adjustment_type: currentAdjustment.adjustment_type,
        is_discount: currentAdjustment.is_discount,
        billing_type: currentAdjustment.billing_type,
        base_category: currentAdjustment.base_category,
        reason: currentAdjustment.reason,
        billing_id: currentAdjustment.billing_id,
        base_amount: currentAdjustment.base_amount,
        applied_amount: result.amount_applied,
        tax_applied: result.tax_applied,
        final_impact: result.final_impact,
      };

      setAdjustments((prev) => [...prev, newAdjustment]);

      await fetchBillingRecordsByOrder(selectedBooking.orderid);
      await fetchAllData();

      if (showInvoiceModal && invoiceData) {
        await fetchInvoiceData(selectedBooking.orderid, 'guest');
      }

      setShowAdjustmentModal(false);

      setCurrentAdjustment({
        amount: '',
        adjustment_type: 'flat',
        is_discount: true,
        billing_type: 'Manual Adjustment - Discount',
        base_category: '',
        reason: '',
        is_taxable: true,
        override_tax_amount: null,
        base_amount: null,
        billing_id: null,
      });

      setEmailStatus({
        success: true,
        message: 'Adjustment added successfully',
      });
      setTimeout(() => setEmailStatus(null), 3000);
    } catch (err) {
      console.error('Error adding adjustment:', err);
      setEmailStatus({
        success: false,
        message: err.message || 'Failed to add adjustment',
      });
      setTimeout(() => setEmailStatus(null), 3000);
    }
  };

  const handleUpdateGST = async (orderId, gstNumber) => {
    setUpdatingGST(true);
    setGstErrors('');

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/update-gst/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          order_id: orderId,
          gst_number: gstNumber || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update GST number');
      }

      await response.json();

      setEmailStatus({
        success: true,
        message: `GST number added successfully`,
      });

      await fetchAllData();

      setShowGSTModal(false);
      setGstNumber('');

      setTimeout(() => setEmailStatus(null), 3000);
    } catch (err) {
      console.error('Error updating GST:', err);
      setGstErrors(err.message || 'Failed to update GST number');
      setEmailStatus({
        success: false,
        message: err.message || 'Failed to update GST number',
      });
      setTimeout(() => setEmailStatus(null), 3000);
    } finally {
      setUpdatingGST(false);
    }
  };

  const handleOpenGSTModal = (booking) => {
    setSelectedBooking(booking);
    const existingGST = booking.gst_number || '';
    setGstNumber(existingGST);
    setGstErrors('');
    setShowGSTModal(true);
  };

  const handleDownloadPDF = async (orderId, type) => {
    try {
      let url = `${BQ_BASE_URL}/bq/api/invoice/pdf/${orderId}?format=folio`;

      if (type === 'guest') {
        if (selectedBillingType === 'room_only') {
          url += `&room_rent=true&food_order=false&amenity_service=true&room_service=false&full_stay=false`;
        } else if (selectedBillingType === 'full_stay') {
          url += `&room_rent=true&food_order=true&amenity_service=true&room_service=true&full_stay=true`;
        } else if (selectedBillingType === 'custom') {
          url += `&room_rent=${includeCharges.room}`;
          url += `&food_order=${includeCharges.food}`;
          url += `&amenity_service=${includeCharges.amenities}`;
          url += `&room_service=${includeCharges.laundry || false}`;
          url += `&full_stay=false`;
        }
        url += buildExcludedSuffix();
      }

      const response = await fetch(url, { headers: { accept: '*/*' } });
      if (!response.ok) throw new Error('Failed to download PDF');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `invoice_${orderId}_${type}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download invoice');
    }
  };

  const handleEmailInvoice = (orderId, guestEmail) => {
    setSelectedBooking({ order_id: orderId, guest_email: guestEmail });
    setEmailOption('guest');
    setCustomEmail(guestEmail);
    setEmailError('');
    setShowEmailModal(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (page, newItemsPerPage = null) => {
    if (newItemsPerPage !== null) {
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1);
    } else {
      setCurrentPage(page);
    }
  };

  const handleToggleFoodInvoice = (foodInvoiceId) => {
    setExcludedFoodInvoices((prev) =>
      prev.includes(foodInvoiceId)
        ? prev.filter((id) => id !== foodInvoiceId)
        : [...prev, foodInvoiceId]
    );
  };

  const getStatusBadge = (bookingStatus, billing) => {
    if (bookingStatus === 'Soft') {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
          Reserved
        </span>
      );
    } else if (bookingStatus === 'Hard') {
      if (billing?.is_fully_paid_or_over) {
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
            Check In
          </span>
        );
      } else if (billing?.total_billed > 0) {
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
            Check In
          </span>
        );
      } else {
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
            Check In
          </span>
        );
      }
    } else if (bookingStatus === 'CheckedOut') {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
          Checked Out
        </span>
      );
    } else if (bookingStatus === 'Cancelled') {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
          Cancelled
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
        {bookingStatus}
      </span>
    );
  };

  const summary = {
    totalRevenue:
      typeof dashboardOverview.total_revenue === 'number'
        ? dashboardOverview.total_revenue
        : parseFloat(dashboardOverview.total_revenue) || 0,
    totalCollected:
      typeof dashboardOverview.total_collected === 'number'
        ? dashboardOverview.total_collected
        : parseFloat(dashboardOverview.total_collected) || 0,
    totalRefunded:
      typeof dashboardOverview.total_refunded === 'number'
        ? dashboardOverview.total_refunded
        : parseFloat(dashboardOverview.total_refunded) || 0,
    netOutstanding:
      typeof dashboardOverview.net_outstanding === 'number'
        ? dashboardOverview.net_outstanding
        : parseFloat(dashboardOverview.net_outstanding) || 0,
    totalAdjustments:
      typeof dashboardOverview.total_adjustments === 'number'
        ? dashboardOverview.total_adjustments
        : parseFloat(dashboardOverview.total_adjustments) || 0,
    adjustmentCharges:
      typeof dashboardOverview.adjustment_charges === 'number'
        ? dashboardOverview.adjustment_charges
        : parseFloat(dashboardOverview.adjustment_charges) || 0,
    adjustmentDiscounts:
      typeof dashboardOverview.adjustment_discounts === 'number'
        ? dashboardOverview.adjustment_discounts
        : parseFloat(dashboardOverview.adjustment_discounts) || 0,
    totalVouchers:
      typeof dashboardOverview.total_vouchers === 'number'
        ? dashboardOverview.total_vouchers
        : parseFloat(dashboardOverview.total_vouchers) || 0,
    paidBookings:
      typeof dashboardOverview.paid_bookings === 'number'
        ? dashboardOverview.paid_bookings
        : parseInt(dashboardOverview.paid_bookings) || 0,
    totalBookings:
      (typeof dashboardOverview.paid_bookings === 'number'
        ? dashboardOverview.paid_bookings
        : parseInt(dashboardOverview.paid_bookings) || 0) +
      (typeof dashboardOverview.pending_bookings === 'number'
        ? dashboardOverview.pending_bookings
        : parseInt(dashboardOverview.pending_bookings) || 0),
    pendingBookings:
      typeof dashboardOverview.pending_bookings === 'number'
        ? dashboardOverview.pending_bookings
        : parseInt(dashboardOverview.pending_bookings) || 0,
    todayCheckins:
      typeof dashboardOverview.today_checkins === 'number'
        ? dashboardOverview.today_checkins
        : parseInt(dashboardOverview.today_checkins) || 0,
    todayCheckouts:
      typeof dashboardOverview.today_checkouts === 'number'
        ? dashboardOverview.today_checkouts
        : parseInt(dashboardOverview.today_checkouts) || 0,
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const foodInvoicesTotal = foodInvoicesForBooking
    .filter((fi) => !excludedFoodInvoices.includes(fi.id))
    .reduce((sum, fi) => sum + Number(fi.total_amount || 0), 0);

  if (loading && allBookings.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 relative">
      {/* Email Status Toast */}
      {emailStatus && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
            emailStatus.success
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          <div className="flex items-center">
            {emailStatus.success ? (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            )}
            {emailStatus.message}
          </div>
        </div>
      )}

      {/* Header with Filters */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
        <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto flex-wrap">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by Reservation No., Guest Name, Room No..."
              className="pl-10 pr-4 py-2 border border-light-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent w-80"
              defaultValue={filters.search}
              onChange={(e) => debouncedSearch(e.target.value)}
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <svg
                className="w-4 h-4 text-dark-gray"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>

          <select
            className="border border-light-gray rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
            value={filters.bookingStatus}
            onChange={(e) => handleFilterChange('bookingStatus', e.target.value)}
          >
            <option value="">All Booking Status</option>
            <option value="Soft">Reserved</option>
            <option value="Hard">Checked In</option>
            <option value="Checkedout">Checked Out</option>
            <option value="Cancelled">Cancelled</option>
          </select>

          <select
            className="border border-light-gray rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
            value={filters.paymentStatus}
            onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
          >
            <option value="">All Payment Status</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="unpaid">Unpaid</option>
          </select>

          <select
            className="border border-light-gray rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
            value={filters.checkoutDateFilter}
            onChange={(e) => {
              handleFilterChange('checkoutDateFilter', e.target.value);
              if (e.target.value) {
                setFilters((prev) => ({
                  ...prev,
                  dateRange: { startDate: '', endDate: '' },
                }));
              }
            }}
          >
            <option value="">All Checkout Dates</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="this_week">This Week</option>
            <option value="this_month">This Month</option>
          </select>

          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="date"
                value={filters.dateRange.startDate}
                onChange={(e) => {
                  setFilters((prev) => ({
                    ...prev,
                    dateRange: {
                      ...prev.dateRange,
                      startDate: e.target.value,
                    },
                    checkoutDateFilter: '',
                  }));
                  setCurrentPage(1);
                }}
                className="border border-light-gray rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="Start Date"
              />
            </div>
            <span className="text-dark-gray">to</span>
            <div className="relative">
              <input
                type="date"
                value={filters.dateRange.endDate}
                onChange={(e) => {
                  setFilters((prev) => ({
                    ...prev,
                    dateRange: {
                      ...prev.dateRange,
                      endDate: e.target.value,
                    },
                    checkoutDateFilter: '',
                  }));
                  setCurrentPage(1);
                }}
                className="border border-light-gray rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="End Date"
              />
            </div>

            {(filters.dateRange.startDate ||
              filters.dateRange.endDate ||
              filters.checkoutDateFilter) && (
              <button
                onClick={() => {
                  setFilters((prev) => ({
                    ...prev,
                    dateRange: { startDate: '', endDate: '' },
                    checkoutDateFilter: '',
                  }));
                  setCurrentPage(1);
                }}
                className="p-2 text-white hover:text-gray-100 transition-colors px-4 py-2 bg-red-600 rounded-lg"
                title="Clear all filters"
              >
                Clear All
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-gray-50 p-4 rounded-lg border border-light-gray">
          <h3 className="text-dark-gray text-sm font-medium mb-1">Total Revenue</h3>
          <p className="text-black text-2xl font-bold">₹{(summary.totalRevenue || 0).toFixed(2)}</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg border border-light-gray">
          <h3 className="text-dark-gray text-sm font-medium mb-1">Total Collected</h3>
          <p className="text-green-600 text-2xl font-bold">
            ₹{(summary.totalCollected || 0).toFixed(2)}
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg border border-light-gray">
          <h3 className="text-dark-gray text-sm font-medium mb-1">Total Refunded</h3>
          <p className="text-red-600 text-2xl font-bold">
            ₹{(summary.totalRefunded || 0).toFixed(2)}
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg border border-light-gray relative group">
          <h3 className="text-dark-gray text-sm font-medium mb-1 flex items-center">
            Total Adjustments
            <div className="relative ml-1">
              <svg
                className="w-4 h-4 text-gray-400 cursor-help"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded-lg py-2 px-3 w-48 z-10">
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Adjustment Charges:</span>
                    <span className="font-semibold">₹{summary.adjustmentCharges.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Adjustment Discounts:</span>
                    <span className="font-semibold text-green-400">
                      ₹{summary.adjustmentDiscounts.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>
          </h3>
          <p className="text-purple-600 text-2xl font-bold">
            ₹{summary.totalAdjustments.toFixed(2)}
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg border border-light-gray">
          <h3 className="text-dark-gray text-sm font-medium mb-1">Total Vouchers:</h3>
          <p className="text-green-600 text-2xl font-bold">{summary.totalVouchers.toFixed(2)}</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg border border-light-gray">
          <h3 className="text-dark-gray text-sm font-medium mb-1">Paid Reservations</h3>
          <p className="text-green-600 text-2xl font-bold">
            {summary.paidBookings || 0}/{summary.totalBookings || 0}
          </p>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg border border-light-gray">
          <h3 className="text-dark-gray text-sm font-medium mb-1">Pending Reservations</h3>
          <p className="text-orange-600 text-2xl font-bold">{summary.pendingBookings || 0}</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg border border-light-gray">
          <h3 className="text-dark-gray text-sm font-medium mb-1">Today's Check-ins</h3>
          <p className="text-blue-600 text-2xl font-bold">{summary.todayCheckins || 0}</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg border border-light-gray">
          <h3 className="text-dark-gray text-sm font-medium mb-1">Today's Check-outs</h3>
          <p className="text-indigo-600 text-2xl font-bold">{summary.todayCheckouts || 0}</p>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-light-gray">
              <th className="text-left py-3 px-2 font-semibold text-black">Reservation No.</th>
              <th className="text-left py-3 px-2 font-semibold text-black">Guest Name</th>
              <th className="text-left py-3 px-2 font-semibold text-black">Room</th>
              <th className="text-left py-3 px-2 font-semibold text-black">Check In/Out</th>
              <th className="text-left py-3 px-2 font-semibold text-black">Status</th>
              <th className="text-left py-3 px-2 font-semibold text-black">Total</th>
              <th className="text-left py-3 px-2 font-semibold text-black">Paid</th>
              <th className="text-left py-3 px-2 font-semibold text-black">Due</th>
              <th className="text-left py-3 px-2 font-semibold text-black">GST</th>
              <th className="text-left py-3 px-2 font-semibold text-black">Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayedBookings.map((booking, index) => (
              <tr
                key={booking.bookingid}
                className={`border-b border-light-gray ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
              >
                <td className="py-3 px-4 text-dark-gray">{booking.orderid}</td>
                <td className="py-3 px-4 text-black">
                  {booking.firstname} {booking.lastname}
                  <div className="text-xs text-dark-gray">{booking.emailid}</div>
                </td>
                <td className="py-3 px-2">
                  <div className="text-black">Room {booking.roomnumber || 'N/A'}</div>
                  <div className="text-xs text-dark-gray">{booking.roomtypename}</div>
                </td>
                <td className="py-3 px-2 text-dark-gray">
                  <div>{booking.checkindate}</div>
                  <span className="font-bold">to</span>
                  <div> {booking.checkoutdate}</div>
                </td>
                <td className="py-3 px-2">
                  {getStatusBadge(booking.booking_status, booking.billing)}
                </td>
                <td className="py-3 px-2 text-black font-medium">
                  ₹{booking.billing?.total_billed?.toFixed(2) || '0.00'}
                </td>
                <td className="py-3 px-2 text-green-600 font-medium">
                  ₹{booking.billing?.total_paid?.toFixed(2) || '0.00'}
                </td>
                <td className="py-3 px-2">
                  {booking.billing?.net_payable > 0 ? (
                    <span className="text-red-600 font-medium">
                      ₹{booking.billing.net_payable.toFixed(2)}
                    </span>
                  ) : (
                    <span className="text-green-600">-</span>
                  )}
                </td>
                <td className="py-3 px-2">
                  {booking.gst_number && booking.gst_number !== '' ? (
                    <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded border border-gray-200">
                      {booking.gst_number}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">Not added</span>
                  )}
                </td>
                <td className="py-3 px-2">
                  {booking.booking_status !== 'Soft' ? (
                    <div className="flex space-x-2">
                      <button
                        onClick={() =>
                          navigate('/dashboard/core-modules/finance/adjustment-details', {
                            state: { booking },
                          })
                        }
                        className="text-orange-600 hover:text-orange-800 transition-colors"
                        title="View Adjustment Details"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </button>

                      <button
                        onClick={() => handleViewHotelInvoice(booking)}
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                        title="View Hotel Invoice (Full Details)"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
                          />
                        </svg>
                      </button>

                      <button
                        onClick={() => handleGenerateInvoice(booking)}
                        className="text-green-600 hover:text-green-800 transition-colors"
                        title="Generate Guest Invoice with Adjustments"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </button>

                      <button
                        onClick={() => handleDownloadPDF(booking.orderid, 'hotel')}
                        className="text-gray-600 hover:text-gray-800 transition-colors"
                        title="Download PDF"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleOpenGSTModal(booking)}
                        className="text-indigo-600 hover:text-indigo-800 transition-colors"
                        title="Add/Update GST Number"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <circle cx="12" cy="12" r="12" strokeWidth={1.5} fill="none" />
                          <text
                            x="12"
                            y="17"
                            textAnchor="middle"
                            fill="currentColor"
                            fontSize="12"
                            fontWeight="semibold"
                          >
                            GST
                          </text>
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <span className="text-gray-400 text-xs">No actions</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {displayedBookings.length === 0 && !loading && (
          <div className="text-center py-8 text-dark-gray">
            No bookings found matching your criteria
          </div>
        )}
      </div>

      {totalPages > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      )}

      {/* Billing Type Selection Modal */}
      {showBillingTypeModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-black mb-4">Invoice Assistant</h3>
            <p className="text-dark-gray mb-4">
              Guest Name: {selectedBooking.firstname} {selectedBooking.lastname}
              <br />
              Reservation Number: {selectedBooking.orderid}
            </p>

            <div className="space-y-4">
              <div className="flex flex-col md:flex-row md:space-x-4">
                <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 flex-1">
                  <input
                    type="radio"
                    name="billingType"
                    value="full_stay"
                    checked={selectedBillingType === 'full_stay'}
                    onChange={(e) => setSelectedBillingType(e.target.value)}
                    className="w-5 h-5 mr-3 accent-black"
                  />
                  <div>
                    <span className="font-medium text-black">Guest Invoice</span>
                    <p className="text-sm text-dark-gray">
                      Complete invoice including all charges (room, food, amenities, services)
                    </p>
                  </div>
                </label>

                <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 flex-1">
                  <input
                    type="radio"
                    name="billingType"
                    value="custom"
                    checked={selectedBillingType === 'custom'}
                    onChange={(e) => setSelectedBillingType(e.target.value)}
                    className="w-5 h-5 mr-3 accent-black"
                  />
                  <div>
                    <span className="font-medium text-black">Custom Invoice</span>
                    <p className="text-sm text-dark-gray">
                      Select specific charges to include in the invoice
                    </p>
                  </div>
                </label>
              </div>

              {selectedBillingType === 'custom' && (
                <div className="border rounded-lg p-4 space-y-1 text-sm">
                  <div className="flex items-center justify-between mb-2 border-b pb-1">
                    <span className="font-medium text-black">Select Charges to Include</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={Object.values(includeCharges).every((v) => v)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setIncludeCharges({
                            room: checked,
                            food: checked,
                            laundry: checked,
                            amenities: checked,
                            other: checked,
                          });
                        }}
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                    </label>
                  </div>

                  <label className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={includeCharges.room}
                      onChange={(e) =>
                        setIncludeCharges({
                          ...includeCharges,
                          room: e.target.checked,
                        })
                      }
                      className="w-4 h-4 mr-3 accent-black"
                    />
                    <span className="text-black">Room Charges</span>
                  </label>

                  {/* Food & Beverages with per-invoice +/- */}
                  <div className="p-2 rounded">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeCharges.food}
                        onChange={(e) =>
                          setIncludeCharges({
                            ...includeCharges,
                            food: e.target.checked,
                          })
                        }
                        className="w-4 h-4 mr-3 accent-black"
                      />
                      <span className="text-black font-medium">Food &amp; Beverages</span>
                      {foodInvoicesForBooking.length > 0 && includeCharges.food && (
                        <span className="ml-auto text-xs text-dark-gray">
                          Selected: ₹{foodInvoicesTotal.toFixed(2)}
                        </span>
                      )}
                    </label>

                    {includeCharges.food && (
                      <div className="ml-7 mt-2 space-y-1">
                        {loadingFoodInvoices && (
                          <div className="text-xs text-dark-gray italic">Loading food bills…</div>
                        )}
                        {!loadingFoodInvoices && foodInvoicesForBooking.length === 0 && (
                          <div className="text-xs text-dark-gray italic">
                            No food bills found for this booking.
                          </div>
                        )}
                        {!loadingFoodInvoices &&
                          foodInvoicesForBooking.map((fi) => {
                            const isIncluded = !excludedFoodInvoices.includes(fi.id);
                            return (
                              <div
                                key={fi.id}
                                className={`flex items-center justify-between p-2 rounded border ${
                                  isIncluded
                                    ? 'border-green-200 bg-green-50'
                                    : 'border-red-200 bg-red-50'
                                }`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-mono text-black truncate">
                                    Bill No: {fi.bill_number}
                                  </div>
                                  {fi.kot_number && (
                                    <div className="text-[10px] text-dark-gray">
                                      KOT: {fi.kot_number}
                                    </div>
                                  )}
                                </div>
                                <div className="text-xs font-medium text-black mr-2">
                                  ₹{Number(fi.total_amount || 0).toFixed(2)}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleToggleFoodInvoice(fi.id)}
                                  className={`w-7 h-7 rounded-full font-bold text-sm flex items-center justify-center transition-colors ${
                                    isIncluded
                                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                                  }`}
                                  title={isIncluded ? 'Remove from invoice' : 'Add back to invoice'}
                                >
                                  {isIncluded ? '−' : '+'}
                                </button>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>

                  <label className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={includeCharges.laundry}
                      onChange={(e) =>
                        setIncludeCharges({
                          ...includeCharges,
                          laundry: e.target.checked,
                        })
                      }
                      className="w-4 h-4 mr-3 accent-black"
                    />
                    <span className="text-black">Laundry</span>
                  </label>

                  <label className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={includeCharges.amenities}
                      onChange={(e) =>
                        setIncludeCharges({
                          ...includeCharges,
                          amenities: e.target.checked,
                        })
                      }
                      className="w-4 h-4 mr-3 accent-black"
                    />
                    <span className="text-black">Amenities</span>
                  </label>
                </div>
              )}

              <div className="border-t pt-1">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showDiscount}
                    onChange={(e) => setShowDiscount(e.target.checked)}
                    className="w-4 h-4 mr-3 accent-black"
                  />
                  <span className="text-black">Show existing adjustments on invoice</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowBillingTypeModal(false)}
                className="px-4 py-2 border border-light-gray rounded-lg text-dark-gray hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBillingTypeSelect}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
              >
                Generate Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Adjustment Modal */}
      {showAdjustmentModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-black">Add Manual Adjustment</h3>
              <button
                onClick={() => setShowAdjustmentModal(false)}
                className="text-dark-gray hover:text-black"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="mb-4 text-sm text-dark-gray">
              <p>
                <span className="font-medium">Guest:</span> {selectedBooking.firstname}{' '}
                {selectedBooking.lastname}
              </p>
              <p>
                <span className="font-medium">Reservation:</span> {selectedBooking.orderid}
              </p>
            </div>

            {loadingBillingRecords ? (
              <div className="text-center py-6 text-dark-gray">Loading billing records…</div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-black mb-1">
                    Adjustment type
                  </label>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() =>
                        setCurrentAdjustment((p) => ({ ...p, adjustment_type: 'flat' }))
                      }
                      className={`px-3 py-1 rounded border text-sm ${
                        currentAdjustment.adjustment_type === 'flat'
                          ? 'bg-black text-white'
                          : 'bg-white text-black'
                      }`}
                    >
                      Flat
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setCurrentAdjustment((p) => ({ ...p, adjustment_type: 'percentage' }))
                      }
                      className={`px-3 py-1 rounded border text-sm ${
                        currentAdjustment.adjustment_type === 'percentage'
                          ? 'bg-black text-white'
                          : 'bg-white text-black'
                      }`}
                    >
                      Percentage
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-black mb-1">
                    Discount or charge?
                  </label>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() =>
                        setCurrentAdjustment((p) => ({
                          ...p,
                          is_discount: true,
                          billing_type: 'Manual Adjustment - Discount',
                        }))
                      }
                      className={`px-3 py-1 rounded border text-sm ${
                        currentAdjustment.is_discount
                          ? 'bg-black text-white'
                          : 'bg-white text-black'
                      }`}
                    >
                      Discount
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setCurrentAdjustment((p) => ({
                          ...p,
                          is_discount: false,
                          billing_type: 'Manual Adjustment - Charge',
                        }))
                      }
                      className={`px-3 py-1 rounded border text-sm ${
                        !currentAdjustment.is_discount
                          ? 'bg-black text-white'
                          : 'bg-white text-black'
                      }`}
                    >
                      Charge
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-black mb-1">
                    Amount{currentAdjustment.adjustment_type === 'percentage' ? ' (%)' : ' (₹)'}
                  </label>
                  <input
                    type="number"
                    value={currentAdjustment.amount}
                    onChange={(e) =>
                      setCurrentAdjustment((p) => ({ ...p, amount: e.target.value }))
                    }
                    className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-black mb-1">Category</label>
                  <select
                    value={currentAdjustment.base_category}
                    onChange={(e) =>
                      setCurrentAdjustment((p) => ({ ...p, base_category: e.target.value }))
                    }
                    className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black"
                  >
                    <option value="">Select category</option>
                    <option value="room">Room</option>
                    <option value="food">Food</option>
                    <option value="amenity">Amenity</option>
                    <option value="room_service">Room Service</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-black mb-1">Reason</label>
                  <input
                    type="text"
                    value={currentAdjustment.reason}
                    onChange={(e) =>
                      setCurrentAdjustment((p) => ({ ...p, reason: e.target.value }))
                    }
                    placeholder="Reason for the adjustment"
                    className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={currentAdjustment.is_taxable}
                    onChange={(e) =>
                      setCurrentAdjustment((p) => ({ ...p, is_taxable: e.target.checked }))
                    }
                    className="w-4 h-4 accent-black"
                  />
                  <span className="text-sm text-black">Apply tax</span>
                </div>

                {billingRecords.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-black mb-1">
                      Apply to specific billing record (optional)
                    </label>
                    <select
                      value={currentAdjustment.billing_id || ''}
                      onChange={(e) =>
                        setCurrentAdjustment((p) => ({
                          ...p,
                          billing_id: e.target.value || null,
                        }))
                      }
                      className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black"
                    >
                      <option value="">Not tied to a record</option>
                      {billingRecords.map((br) => (
                        <option key={br.billing_id || br.id} value={br.billing_id || br.id}>
                          {br.billing_type || br.billingtype || 'Billing'} — ₹
                          {Number(br.final_amount || br.finalamount || 0).toFixed(2)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAdjustmentModal(false)}
                className="px-4 py-2 border border-light-gray rounded-lg text-dark-gray hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAdjustment}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
              >
                Add Adjustment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GST Modal */}
      {showGSTModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-black">Update GST Number</h3>
              <button
                onClick={() => {
                  setShowGSTModal(false);
                  setGstNumber('');
                  setGstErrors('');
                }}
                className="text-dark-gray hover:text-black"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-dark-gray text-sm mb-2">
                <span className="font-medium">Reservation Number:</span> {selectedBooking.orderid}
              </p>
              <p className="text-dark-gray text-sm mb-2">
                <span className="font-medium">Guest Name:</span> {selectedBooking.firstname}{' '}
                {selectedBooking.lastname}
              </p>
              {selectedBooking.gst_number && (
                <p className="text-dark-gray text-sm mb-4">
                  <span className="font-medium">Current GST:</span> {selectedBooking.gst_number}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GST Number <span className="text-gray-400 text-xs">(optional)</span>
              </label>
              <input
                type="text"
                value={gstNumber}
                onChange={handleGstChange}
                placeholder="Enter GST number (e.g., 22AAAAA0000A1Z5)"
                className="w-full p-2 border border-gray-300 rounded-md text-sm uppercase focus:outline-none focus:ring-2 focus:ring-black"
              />
              {gstErrors && <p className="text-xs text-red-500 mt-1">{gstErrors}</p>}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowGSTModal(false);
                  setGstNumber('');
                  setGstErrors('');
                }}
                className="px-4 py-2 border border-light-gray rounded-lg text-dark-gray hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdateGST(selectedBooking.orderid, gstNumber || null)}
                disabled={updatingGST || (gstNumber && gstErrors)}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {updatingGST ? 'Updating...' : 'Update GST Number'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-black">Send Invoice Email</h3>
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setCustomEmail('');
                  setEmailError('');
                }}
                className="text-dark-gray hover:text-black"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-dark-gray text-sm mb-2">
                <span className="font-medium">Reservation Number:</span> {selectedBooking.order_id}
              </p>
              <p className="text-dark-gray text-sm mb-4">
                <span className="font-medium">Invoice Type:</span> Guest Invoice
              </p>
            </div>

            <div className="space-y-3">
              <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="emailOption"
                  value="guest"
                  checked={emailOption === 'guest'}
                  onChange={() => {
                    setEmailOption('guest');
                    setCustomEmail(selectedBooking.guest_email);
                    setEmailError('');
                  }}
                  className="w-5 h-5 mt-0.5 mr-3 accent-black"
                />
                <div className="flex-1">
                  <span className="font-medium text-black">Send to Guest's Email</span>
                  <p className="text-sm text-dark-gray">{selectedBooking.guest_email}</p>
                </div>
              </label>

              <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="emailOption"
                  value="custom"
                  checked={emailOption === 'custom'}
                  onChange={() => {
                    setEmailOption('custom');
                    setCustomEmail('');
                    setEmailError('');
                  }}
                  className="w-5 h-5 mt-0.5 mr-3 accent-black"
                />
                <div className="flex-1">
                  <span className="font-medium text-black">Send to Different Email</span>
                  <p className="text-sm text-dark-gray">Enter email address below</p>
                </div>
              </label>

              {emailOption === 'custom' && (
                <div className="mt-2 pl-8">
                  <input
                    type="email"
                    value={customEmail}
                    onChange={(e) => {
                      setCustomEmail(e.target.value);
                      setEmailError(validateEmail(e.target.value));
                    }}
                    placeholder="Enter email address"
                    className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black"
                  />
                  {emailError && <p className="text-xs text-red-500 mt-1">{emailError}</p>}
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setCustomEmail('');
                  setEmailError('');
                }}
                className="px-4 py-2 border border-light-gray rounded-lg text-dark-gray hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (emailOption === 'custom' && !customEmail) {
                    setEmailError('Please enter an email address');
                    return;
                  }
                  if (emailOption === 'custom' && validateEmail(customEmail)) {
                    setEmailError(validateEmail(customEmail));
                    return;
                  }
                  const recipientEmail =
                    emailOption === 'guest' ? selectedBooking.guest_email : customEmail;
                  handleSendEmailWithCustom(selectedBooking.order_id, recipientEmail);
                }}
                disabled={
                  emailSending || (emailOption === 'custom' && (!customEmail || emailError))
                }
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {emailSending ? 'Sending...' : 'Send Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && invoiceData && (
        <InvoiceModal
          invoiceData={invoiceData}
          invoiceType={invoiceType}
          onClose={() => {
            setShowInvoiceModal(false);
            setInvoiceData(null);
          }}
          onDownload={() => handleDownloadPDF(invoiceData.order_id, invoiceType)}
          onEmail={() => handleEmailInvoice(invoiceData.order_id, invoiceData.guest_email)}
          adjustments={adjustments}
          loading={loadingInvoice}
          emailSending={emailSending}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Invoice Modal
// ─────────────────────────────────────────────
function InvoiceModal({
  invoiceData,
  invoiceType,
  onClose,
  onDownload,
  onEmail,
  adjustments,
  loading,
  emailSending,
}) {
  const isHotelInvoice = invoiceType === 'hotel';
  const [showAdjustments, setShowAdjustments] = useState(true);

  const displayLines = isHotelInvoice
    ? invoiceData.folio_lines
    : invoiceData.filtered_folio_lines || invoiceData.folio_lines || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr.includes('/')) return dateStr;
    return new Date(dateStr).toLocaleDateString();
  };

  // helper: FOOD BILL memo lines should not print anything in the Balance column
  const isFoodBillLine = (description) =>
    String(description || '')
      .toUpperCase()
      .trim()
      .startsWith('FOOD BILL');

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
          <p className="text-center mt-4">Loading invoice...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
      <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="flex items-center space-x-3">
              <h2 className="text-2xl font-bold text-black">
                {isHotelInvoice ? 'Hotel Invoice' : 'Guest Invoice'}
              </h2>
              {!isHotelInvoice && invoiceData.billingType && (
                <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded capitalize">
                  {invoiceData.billingType.replace('_', ' ')}
                </span>
              )}
            </div>
            <p className="text-dark-gray mt-1">Invoice #: {invoiceData.invoice_number}</p>
            <p className="text-dark-gray text-sm">Reservation No. #: {invoiceData.order_id}</p>
            <p className="text-dark-gray text-sm">
              Issued: {new Date(invoiceData.invoice_date).toLocaleString()}
            </p>
            {invoiceData.bill_prepared_in && (
              <p className="text-dark-gray text-sm">
                Bill Prepared In: {invoiceData.bill_prepared_in}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-dark-gray hover:text-black">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-black mb-2">{invoiceData.property_name}</h3>
            <p className="text-dark-gray text-sm">{invoiceData.property_address}</p>
            <p className="text-dark-gray text-sm">Contact: {invoiceData.property_contact}</p>
            <p className="text-dark-gray text-sm">Email: {invoiceData.property_email}</p>
            {invoiceData.property_gst && (
              <p className="text-dark-gray text-sm">GST: {invoiceData.property_gst}</p>
            )}
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-black mb-2">Guest Details</h3>
            <p className="text-black font-medium">{invoiceData.guest_name}</p>
            <p className="text-dark-gray text-sm">{invoiceData.guest_email}</p>
            <p className="text-dark-gray text-sm">{invoiceData.guest_phone}</p>
            {invoiceData.company_name && (
              <p className="text-dark-gray text-sm">Company: {invoiceData.company_name}</p>
            )}
            {invoiceData.company_gst && (
              <p className="text-dark-gray text-sm">Company GST: {invoiceData.company_gst}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="p-3 border rounded-lg">
            <p className="text-xs text-dark-gray">Room</p>
            <p className="text-black font-medium">
              {invoiceData.room_number} • {invoiceData.room_type}
            </p>
          </div>
          <div className="p-3 border rounded-lg">
            <p className="text-xs text-dark-gray">Check In</p>
            <p className="text-black">{invoiceData.check_in_date}</p>
          </div>
          <div className="p-3 border rounded-lg">
            <p className="text-xs text-dark-gray">Check Out</p>
            <p className="text-black">{invoiceData.check_out_date}</p>
          </div>
          <div className="p-3 border rounded-lg">
            <p className="text-xs text-dark-gray">Pax</p>
            <p className="text-black">
              {invoiceData.total_pax} ({invoiceData.pax_label})
            </p>
          </div>
        </div>

        {!isHotelInvoice && invoiceData.note && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 text-sm flex items-center">
              <svg
                className="w-5 h-5 mr-2 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {invoiceData.note}
            </p>
          </div>
        )}

        <div className="border-t border-light-gray pt-4">
          <h3 className="font-semibold text-black mb-3">Charge Details</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-light-gray bg-gray-50">
                  <th className="text-left py-2 px-3 font-semibold text-black text-sm">Date</th>
                  <th className="text-left py-2 px-3 font-semibold text-black text-sm">HSN/SAC</th>
                  <th className="text-left py-2 px-3 font-semibold text-black text-sm">
                    Description
                  </th>
                  <th className="text-left py-2 px-3 font-semibold text-black text-sm">Mode</th>
                  <th className="text-left py-2 px-3 font-semibold text-black text-sm">Voucher</th>
                  <th className="text-right py-2 px-3 font-semibold text-black text-sm">
                    Charges (₹)
                  </th>
                  <th className="text-right py-2 px-3 font-semibold text-black text-sm">
                    Credit (₹)
                  </th>
                  <th className="text-right py-2 px-3 font-semibold text-black text-sm">
                    Balance (₹)
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayLines.map((line, index) => (
                  <tr
                    key={index}
                    className={`border-b border-light-gray ${
                      line.description.includes('Day Total') || line.description.includes('C/F')
                        ? 'bg-gray-100 font-medium'
                        : ''
                    }`}
                  >
                    <td className="py-2 px-3 text-dark-gray text-sm">{formatDate(line.date)}</td>
                    <td className="py-2 px-3 text-dark-gray text-sm">{line.hsn_sac || '-'}</td>
                    <td className="py-2 px-3 text-black text-sm">{line.description}</td>
                    <td className="py-2 px-3 text-dark-gray text-sm">{line.mode || '-'}</td>
                    <td className="py-2 px-3 text-dark-gray text-sm">{line.voucher || '-'}</td>
                    <td className="py-2 px-3 text-right text-black text-sm">
                      {line.charges && parseFloat(line.charges) > 0
                        ? `₹${parseFloat(line.charges).toFixed(2)}`
                        : '-'}
                    </td>
                    <td className="py-2 px-3 text-right text-dark-gray text-sm">
                      {line.credit && parseFloat(line.credit) > 0
                        ? `₹${parseFloat(line.credit).toFixed(2)}`
                        : '-'}
                    </td>
                    {/* Balance — blank for FOOD BILL memo rows only */}
                    <td className="py-2 px-3 text-right text-black text-sm font-medium">
                      {isFoodBillLine(line.description)
                        ? '-'
                        : `₹${parseFloat(line.balance || 0).toFixed(2)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {!isHotelInvoice && adjustments.length > 0 && (
          <div className="mt-4 border-t border-light-gray pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-black">Manual Adjustments</h3>
              <label className="flex items-center cursor-pointer">
                <span className="text-sm text-dark-gray mr-2">Show on Invoice</span>
                <div className="relative">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={showAdjustments}
                    onChange={(e) => setShowAdjustments(e.target.checked)}
                  />
                  <div
                    className={`block w-10 h-6 rounded-full transition-colors ${
                      showAdjustments ? 'bg-purple-600' : 'bg-gray-300'
                    }`}
                  ></div>
                  <div
                    className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${
                      showAdjustments ? 'transform translate-x-4' : ''
                    }`}
                  ></div>
                </div>
              </label>
            </div>

            {showAdjustments && (
              <div className="space-y-2">
                {adjustments.map((adj) => (
                  <div
                    key={adj.id}
                    className="flex justify-between items-center p-2 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span
                          className={`text-sm font-medium ${
                            adj.is_discount ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {adj.is_discount ? 'Discount' : 'Extra Charge'}
                          {adj.adjustment_type === 'percentage' ? ` (${adj.amount}%)` : ''}
                        </span>
                        <span className="text-xs text-dark-gray ml-2">({adj.reason})</span>
                      </div>
                      {adj.base_category && (
                        <p className="text-xs text-dark-gray">Category: {adj.base_category}</p>
                      )}
                      {adj.tax_applied > 0 && (
                        <p className="text-xs text-dark-gray">Tax: ₹{adj.tax_applied.toFixed(2)}</p>
                      )}
                    </div>
                    <div className="flex items-center">
                      <span
                        className={`font-medium ${
                          adj.is_discount ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {adj.is_discount ? '-' : '+'}₹
                        {Math.abs(adj.final_impact || adj.amount).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <div className="w-80">
            {invoiceData.summary &&
              invoiceData.summary.map((item, index) => (
                <div
                  key={index}
                  className="flex justify-between py-2 border-t border-light-gray first:border-t-0"
                >
                  <span className="text-dark-gray">{item.label}</span>
                  <span className="text-black font-medium">
                    ₹{parseFloat(item.amount).toFixed(2)}
                  </span>
                </div>
              ))}

            {!isHotelInvoice && adjustments && adjustments.length > 0 && (
              <div className="border-t border-dashed border-light-gray pt-2 mt-2">
                <div className="text-xs text-dark-gray mb-1">Manual Adjustments Applied:</div>
                {adjustments.map((adj) => (
                  <div key={adj.id} className="flex justify-between py-1 text-sm">
                    <span className="text-dark-gray">{adj.reason}</span>
                    <span className={adj.is_discount ? 'text-green-600' : 'text-red-600'}>
                      {adj.is_discount ? '-' : '+'}₹{Math.abs(adj.amount).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-between py-2 mt-1 border-t-2 border-black font-bold">
              <span className="text-black text-lg">Total Amount</span>
              <span className="text-black text-lg">
                ₹{parseFloat(invoiceData.grand_total || 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {invoiceData.declaration && (
          <div className="mt-6 pt-4 border-t border-b pb-4 border-light-gray">
            <p className="text-center text-dark-gray text-xs whitespace-pre-line">
              {invoiceData.declaration}
            </p>
          </div>
        )}

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-light-gray rounded-lg text-dark-gray hover:bg-gray-50"
          >
            Close
          </button>
          {!isHotelInvoice && (
            <button
              onClick={onEmail}
              disabled={emailSending}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center disabled:opacity-50"
            >
              {emailSending ? (
                <>
                  <svg
                    className="w-4 h-4 mr-2 animate-spin"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  Email Invoice
                </>
              )}
            </button>
          )}
          <button
            onClick={onDownload}
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
}

export default FinanceAndAccounting;

// import React, { useState, useEffect, useCallback } from 'react';
// import { useNavigate } from 'react-router-dom';
// import debounce from 'lodash/debounce';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// // Pagination Component (unchanged)
// function Pagination({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange }) {
//   const [inputPage, setInputPage] = useState(currentPage);

//   const getPageNumbers = () => {
//     const delta = 2;
//     const range = [];
//     const rangeWithDots = [];
//     let l;

//     for (let i = 1; i <= totalPages; i++) {
//       if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
//         range.push(i);
//       }
//     }

//     range.forEach((i) => {
//       if (l) {
//         if (i - l === 2) {
//           rangeWithDots.push(l + 1);
//         } else if (i - l !== 1) {
//           rangeWithDots.push('...');
//         }
//       }
//       rangeWithDots.push(i);
//       l = i;
//     });

//     return rangeWithDots;
//   };

//   const handleInputChange = (e) => {
//     const value = e.target.value === '' ? '' : parseInt(e.target.value);
//     setInputPage(value);
//   };

//   const handleInputKeyPress = (e) => {
//     if (e.key === 'Enter') {
//       const page = parseInt(inputPage);
//       if (page >= 1 && page <= totalPages) {
//         onPageChange(page);
//       }
//     }
//   };

//   const handleGoToPage = () => {
//     const page = parseInt(inputPage);
//     if (page >= 1 && page <= totalPages) {
//       onPageChange(page);
//     }
//   };

//   return (
//     <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
//       <div className="text-dark-gray text-sm">
//         Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
//         {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
//       </div>

//       <div className="flex items-center space-x-2">
//         <button
//           onClick={() => onPageChange(1)}
//           disabled={currentPage === 1}
//           className={`px-3 py-1 rounded border ${
//             currentPage === 1
//               ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
//               : 'hover:bg-gray-50 text-black'
//           }`}
//           title="First Page"
//         >
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path
//               strokeLinecap="round"
//               strokeLinejoin="round"
//               strokeWidth={2}
//               d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
//             />
//           </svg>
//         </button>

//         <button
//           onClick={() => onPageChange(currentPage - 1)}
//           disabled={currentPage === 1}
//           className={`px-3 py-1 rounded border ${
//             currentPage === 1
//               ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
//               : 'hover:bg-gray-50 text-black'
//           }`}
//         >
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path
//               strokeLinecap="round"
//               strokeLinejoin="round"
//               strokeWidth={2}
//               d="M15 19l-7-7 7-7"
//             />
//           </svg>
//         </button>

//         <div className="hidden sm:flex space-x-1">
//           {getPageNumbers().map((page, index) =>
//             page === '...' ? (
//               <span key={`dots-${index}`} className="px-3 py-1 text-dark-gray">
//                 ...
//               </span>
//             ) : (
//               <button
//                 key={page}
//                 onClick={() => onPageChange(page)}
//                 className={`px-3 py-1 rounded border min-w-[40px] ${
//                   currentPage === page ? 'bg-black text-white' : 'hover:bg-gray-50 text-black'
//                 }`}
//               >
//                 {page}
//               </button>
//             )
//           )}
//         </div>

//         <span className="sm:hidden text-sm text-dark-gray">
//           Page {currentPage} of {totalPages}
//         </span>

//         <button
//           onClick={() => onPageChange(currentPage + 1)}
//           disabled={currentPage === totalPages}
//           className={`px-3 py-1 rounded border ${
//             currentPage === totalPages
//               ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
//               : 'hover:bg-gray-50 text-black'
//           }`}
//         >
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
//           </svg>
//         </button>

//         <button
//           onClick={() => onPageChange(totalPages)}
//           disabled={currentPage === totalPages}
//           className={`px-3 py-1 rounded border ${
//             currentPage === totalPages
//               ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
//               : 'hover:bg-gray-50 text-black'
//           }`}
//           title="Last Page"
//         >
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path
//               strokeLinecap="round"
//               strokeLinejoin="round"
//               strokeWidth={2}
//               d="M13 5l7 7-7 7M5 5l7 7-7 7"
//             />
//           </svg>
//         </button>
//       </div>

//       <div className="flex items-center space-x-3">
//         <select
//           value={itemsPerPage}
//           onChange={(e) => onPageChange(1, parseInt(e.target.value))}
//           className="border border-light-gray rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-black"
//         >
//           <option value={10}>10 / page</option>
//           <option value={25}>25 / page</option>
//           <option value={50}>50 / page</option>
//           <option value={100}>100 / page</option>
//         </select>

//         <div className="flex items-center space-x-1">
//           <span className="text-sm text-dark-gray">Go to</span>
//           <input
//             type="number"
//             min={1}
//             max={totalPages}
//             value={inputPage}
//             onChange={handleInputChange}
//             onKeyPress={handleInputKeyPress}
//             className="w-16 border border-light-gray rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-black"
//             onFocus={(e) => e.target.select()}
//           />
//           <button
//             onClick={handleGoToPage}
//             className="px-3 py-1 bg-black text-white text-sm rounded-lg hover:bg-gray-800"
//           >
//             Go
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// }

// function FinanceAndAccounting() {
//   const navigate = useNavigate();

//   return (
//     <div className="min-h-screen bg-gray-100">
//       <div className="p-6">
//         <header className="flex justify-between items-center mb-3 bg-white p-3">
//           <h1 className="text-black text-xl font-bold ml-2">Finance and Accounting</h1>
//           <div className="flex space-x-6">
//             <button
//               onClick={() => navigate('/dashboard/core-modules/finance/billing-invoicing')}
//               className="btn text-black font-bold"
//             >
//               Billing and Invoicing
//             </button>
//             <button
//               onClick={() =>
//                 navigate('/dashboard/core-modules/finance/accounts-receivable-payable')
//               }
//               className="btn "
//             >
//               Accounts Receivable/Payable
//             </button>
//             <button
//               onClick={() => navigate('/dashboard/core-modules/finance/financial-reporting')}
//               className="btn "
//             >
//               Financial Reporting
//             </button>
//             <button
//               onClick={() => navigate('/dashboard/core-modules/finance/tax-management')}
//               className="btn "
//             >
//               Tax Management
//             </button>
//             <button
//               onClick={() => navigate('/dashboard/core-modules/finance/audit-logs')}
//               className="btn "
//             >
//               Audit Logs
//             </button>
//           </div>
//         </header>

//         <BillingInvoicing />
//       </div>
//     </div>
//   );
// }

// // Billing & Invoicing Component
// function BillingInvoicing() {
//   const navigate = useNavigate();
//   const [allBookings, setAllBookings] = useState([]);
//   const [filteredBookings, setFilteredBookings] = useState([]);
//   const [displayedBookings, setDisplayedBookings] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [staffId] = useState(1);
//   const [uniqueBillingTypes, setUniqueBillingTypes] = useState([]);
//   const [showGSTModal, setShowGSTModal] = useState(false);
//   const [gstNumber, setGstNumber] = useState('');
//   const [gstErrors, setGstErrors] = useState('');
//   const [updatingGST, setUpdatingGST] = useState(false);

//   const [currentPage, setCurrentPage] = useState(1);
//   const [itemsPerPage, setItemsPerPage] = useState(25);
//   const [totalItems, setTotalItems] = useState(0);

//   const [filters, setFilters] = useState({
//     search: '',
//     bookingStatus: '',
//     paymentStatus: '',
//     dateRange: {
//       startDate: '',
//       endDate: '',
//     },
//     // Add checkout date filters
//     checkoutDateFilter: '', // 'today', 'yesterday', 'this_week', 'this_month'
//   });

//   const [selectedBooking, setSelectedBooking] = useState(null);
//   const [showInvoiceModal, setShowInvoiceModal] = useState(false);
//   const [invoiceType, setInvoiceType] = useState('hotel');
//   const [invoiceData, setInvoiceData] = useState(null);
//   const [loadingInvoice, setLoadingInvoice] = useState(false);
//   const [showEmailModal, setShowEmailModal] = useState(false);
//   const [customEmail, setCustomEmail] = useState('');
//   const [emailOption, setEmailOption] = useState('guest');
//   const [emailError, setEmailError] = useState('');

//   const [showBillingTypeModal, setShowBillingTypeModal] = useState(false);
//   const [selectedBillingType, setSelectedBillingType] = useState('room_only');
//   const [includeCharges, setIncludeCharges] = useState({
//     room: true,
//     food: false,
//     laundry: false,
//     amenities: false,
//     other: false,
//   });
//   const [showDiscount, setShowDiscount] = useState(true);

//   const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
//   const [adjustments, setAdjustments] = useState([]);
//   const [billingRecords, setBillingRecords] = useState([]);
//   const [loadingBillingRecords, setLoadingBillingRecords] = useState(false);
//   const [currentAdjustment, setCurrentAdjustment] = useState({
//     amount: '',
//     adjustment_type: 'flat',
//     is_discount: true,
//     billing_type: 'Manual Adjustment - Discount',
//     base_category: '',
//     reason: '',
//     is_taxable: false,
//     override_tax_amount: null,
//     base_amount: null,
//     billing_id: null,
//   });

//   const [emailSending, setEmailSending] = useState(false);
//   const [emailStatus, setEmailStatus] = useState(null);
//   const [filteredOrdersData, setFilteredOrdersData] = useState({
//     orders: {},
//     billings: [],
//     uniqueOrderTypes: [],
//   });
//   const [uniqueOrderTypes, setUniqueOrderTypes] = useState([]);
//   const [ordersData, setOrdersData] = useState({});

//   // New state for dashboard overview
//   // Update the dashboardOverview state (around line 150-160)
//   const [dashboardOverview, setDashboardOverview] = useState({
//     total_revenue: 0,
//     total_collected: 0,
//     total_refunded: 0,
//     net_outstanding: 0,
//     total_adjustments: 0,
//     adjustment_charges: 0, // Add this
//     adjustment_discounts: 0, // Add this
//     total_vouchers: 0, // Add this
//     paid_bookings: 0,
//     pending_bookings: 0,
//     today_checkins: 0,
//     today_checkouts: 0,
//   });

//   const debouncedSearch = useCallback(
//     debounce((value) => {
//       handleFilterChange('search', value);
//     }, 500),
//     []
//   );

//   useEffect(() => {
//     fetchAllData();
//   }, []);

//   useEffect(() => {
//     applyFiltersAndPagination();
//   }, [allBookings, filters, currentPage, itemsPerPage]);

//   // Update the fetchDashboardOverview function to parse numbers properly
//   // Update the fetchDashboardOverview function (around line 230-260)
//   const fetchDashboardOverview = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/dashboard/overview?days=90`, {
//         headers: {
//           accept: 'application/json',
//         },
//       });

//       if (!response.ok) throw new Error('Failed to fetch dashboard overview');

//       const overviewData = await response.json();

//       // Parse all numeric values to ensure they're numbers
//       setDashboardOverview({
//         total_revenue: parseFloat(overviewData.total_revenue) || 0,
//         total_collected: parseFloat(overviewData.total_collected) || 0,
//         total_refunded: parseFloat(overviewData.total_refunded) || 0,
//         net_outstanding: parseFloat(overviewData.net_outstanding) || 0,
//         total_adjustments: parseFloat(overviewData.total_adjustments) || 0,
//         adjustment_charges: parseFloat(overviewData.adjustment_charges) || 0, // Add this
//         adjustment_discounts: parseFloat(overviewData.adjustment_discounts) || 0, // Add this
//         total_vouchers: parseFloat(overviewData.total_vouchers) || 0, // Add this
//         paid_bookings: parseInt(overviewData.paid_bookings) || 0,
//         pending_bookings: parseInt(overviewData.pending_bookings) || 0,
//         today_checkins: parseInt(overviewData.today_checkins) || 0,
//         today_checkouts: parseInt(overviewData.today_checkouts) || 0,
//       });
//     } catch (err) {
//       console.error('Error fetching dashboard overview:', err);
//       // Set default values on error
//       setDashboardOverview({
//         total_revenue: 0,
//         total_collected: 0,
//         total_refunded: 0,
//         net_outstanding: 0,
//         total_adjustments: 0,
//         adjustment_charges: 0,
//         adjustment_discounts: 0,
//         total_vouchers: 0,
//         paid_bookings: 0,
//         pending_bookings: 0,
//         today_checkins: 0,
//         today_checkouts: 0,
//       });
//     }
//   };
//   const fetchAllData = async () => {
//     setLoading(true);
//     try {
//       // Fetch dashboard overview
//       await fetchDashboardOverview();

//       // Fetch bookings from the new dashboard API
//       const bookingsResponse = await fetch(`${BQ_BASE_URL}/bq/api/dashboard/bookings?limit=100`, {
//         headers: {
//           accept: 'application/json',
//         },
//       });

//       if (!bookingsResponse.ok) throw new Error('Failed to fetch bookings');

//       const bookingsData = await bookingsResponse.json();

//       // Ensure bookingsData has the expected structure
//       if (!bookingsData || !bookingsData.bookings) {
//         console.warn('No bookings data received');
//         setAllBookings([]);
//         setLoading(false);
//         return;
//       }

//       // Transform the dashboard booking data to match the expected format
//       const transformedBookings = bookingsData.bookings.map((bookingDetail) => {
//         const guestDetails = bookingDetail.guest_details;
//         const paymentMetrics = bookingDetail.payment_metrics;

//         return {
//           bookingid: guestDetails?.booking_id || '',
//           orderid: guestDetails?.order_id || '',
//           firstname: guestDetails?.guest_name?.split(' ')[0] || '',
//           lastname: guestDetails?.guest_name?.split(' ').slice(1).join(' ') || '',
//           emailid: guestDetails?.email || '',
//           phonenumber: guestDetails?.phone || '',
//           roomnumber: guestDetails?.room_number || '',
//           roomtypename: guestDetails?.room_type || '',
//           checkindate: guestDetails?.check_in || '',
//           checkoutdate: guestDetails?.check_out || '',
//           booking_status: guestDetails?.booking_status || '',
//           booking_type: guestDetails?.booking_type || '',
//           numberofguests: guestDetails?.number_of_guests || 1,
//           lengthofstay: guestDetails?.length_of_stay || 0,
//           reservationmadeon: guestDetails?.reservation_made_on || '',
//           gst_number: guestDetails?.gst_number || '',
//           billing: {
//             total_billed: parseFloat(paymentMetrics?.bill_amount) || 0,
//             total_paid: parseFloat(paymentMetrics?.paid_amount) || 0,
//             net_payable: parseFloat(paymentMetrics?.balance) || 0,
//             is_fully_paid_or_over: paymentMetrics?.status === 'PAID',
//             total_adjustment_amount: parseFloat(paymentMetrics?.total_discounts) || 0,
//             has_adjustments: (bookingDetail.adjustments?.length || 0) > 0,
//           },
//           adjustments: bookingDetail.adjustments || [],
//           reversals: bookingDetail.reversals || [],
//           refunds: bookingDetail.refunds || [],
//           vouchers: bookingDetail.vouchers || [],
//         };
//       });

//       setAllBookings(transformedBookings);
//     } catch (err) {
//       setError(err.message);
//       console.error('Error fetching data:', err);
//       // Set empty array on error to prevent further errors
//       setAllBookings([]);
//     } finally {
//       setLoading(false);
//     }
//   };
//   const applyFiltersAndPagination = () => {
//     const filtered = allBookings.filter((item) => {
//       const searchLower = filters.search.toLowerCase();
//       const matchesSearch =
//         filters.search === '' ||
//         item.bookingid?.toLowerCase().includes(searchLower) ||
//         item.orderid?.toLowerCase().includes(searchLower) ||
//         item.firstname?.toLowerCase().includes(searchLower) ||
//         item.lastname?.toLowerCase().includes(searchLower) ||
//         item.emailid?.toLowerCase().includes(searchLower) ||
//         // Add room number search
//         item.roomnumber?.toLowerCase().includes(searchLower) ||
//         // Add room type search
//         item.roomtypename?.toLowerCase().includes(searchLower);

//       const matchesBookingStatus =
//         filters.bookingStatus === '' || item.booking_status === filters.bookingStatus;

//       const matchesPaymentStatus =
//         filters.paymentStatus === '' ||
//         (filters.paymentStatus === 'paid' && item.billing?.is_fully_paid_or_over) ||
//         (filters.paymentStatus === 'pending' &&
//           !item.billing?.is_fully_paid_or_over &&
//           item.billing?.total_billed > 0) ||
//         (filters.paymentStatus === 'unpaid' && item.billing?.total_billed === 0);

//       let matchesDateRange = true;
//       if (filters.dateRange.startDate || filters.dateRange.endDate) {
//         const bookingCheckin = new Date(item.checkindate);
//         bookingCheckin.setHours(0, 0, 0, 0);

//         if (filters.dateRange.startDate && filters.dateRange.endDate) {
//           const startDate = new Date(filters.dateRange.startDate);
//           startDate.setHours(0, 0, 0, 0);
//           const endDate = new Date(filters.dateRange.endDate);
//           endDate.setHours(23, 59, 59, 999);

//           matchesDateRange = bookingCheckin >= startDate && bookingCheckin <= endDate;
//         } else if (filters.dateRange.startDate) {
//           const startDate = new Date(filters.dateRange.startDate);
//           startDate.setHours(0, 0, 0, 0);
//           matchesDateRange = bookingCheckin >= startDate;
//         } else if (filters.dateRange.endDate) {
//           const endDate = new Date(filters.dateRange.endDate);
//           endDate.setHours(23, 59, 59, 999);
//           matchesDateRange = bookingCheckin <= endDate;
//         }
//       }

//       // Add checkout date filter
//       let matchesCheckoutDate = true;
//       if (filters.checkoutDateFilter) {
//         const checkoutDate = new Date(item.checkoutdate);
//         checkoutDate.setHours(0, 0, 0, 0);
//         const today = new Date();
//         today.setHours(0, 0, 0, 0);

//         switch (filters.checkoutDateFilter) {
//           case 'today':
//             matchesCheckoutDate = checkoutDate.getTime() === today.getTime();
//             break;
//           case 'yesterday': {
//             const yesterday = new Date(today);
//             yesterday.setDate(yesterday.getDate() - 1);
//             matchesCheckoutDate = checkoutDate.getTime() === yesterday.getTime();
//             break;
//           }
//           case 'this_week': {
//             const startOfWeek = new Date(today);
//             startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
//             const endOfWeek = new Date(today);
//             endOfWeek.setDate(today.getDate() + (6 - today.getDay())); // Saturday
//             matchesCheckoutDate = checkoutDate >= startOfWeek && checkoutDate <= endOfWeek;
//             break;
//           }
//           case 'this_month': {
//             const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
//             const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
//             matchesCheckoutDate = checkoutDate >= startOfMonth && checkoutDate <= endOfMonth;
//             break;
//           }
//           default:
//             matchesCheckoutDate = true;
//         }
//       }

//       return (
//         matchesSearch &&
//         matchesBookingStatus &&
//         matchesPaymentStatus &&
//         matchesDateRange &&
//         matchesCheckoutDate
//       );
//     });

//     setFilteredBookings(filtered);
//     setTotalItems(filtered.length);

//     const startIndex = (currentPage - 1) * itemsPerPage;
//     const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage);

//     setDisplayedBookings(paginatedData);
//   };

//   const fetchBillingRecordsByOrder = async (orderId) => {
//     setLoadingBillingRecords(true);
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/billing/by-order/${orderId}`, {
//         headers: {
//           accept: 'application/json',
//         },
//       });

//       if (!response.ok) throw new Error('Failed to fetch billing records');

//       const data = await response.json();
//       setOrdersData(data.orders || {});
//       setUniqueOrderTypes(data.unique_order_types || []);

//       const filteredOrders = {};
//       if (data.orders) {
//         Object.keys(data.orders).forEach((orderType) => {
//           if (orderType !== 'Manual Adjustment') {
//             filteredOrders[orderType] = data.orders[orderType];
//           }
//         });
//       }

//       const filteredBillings =
//         data.billings?.filter(
//           (billing) => !billing.billing_type?.toLowerCase().includes('manual adjustment')
//         ) || [];

//       setBillingRecords(filteredBillings);

//       const uniqueOrderTypes =
//         data.unique_order_types?.filter((type) => type !== 'Manual Adjustment') || [];

//       setFilteredOrdersData({
//         orders: filteredOrders,
//         billings: filteredBillings,
//         uniqueOrderTypes: uniqueOrderTypes,
//       });

//       return {
//         ...data,
//         orders: filteredOrders,
//         billings: filteredBillings,
//         unique_order_types: uniqueOrderTypes,
//       };
//     } catch (err) {
//       console.error('Error fetching billing records:', err);
//       setEmailStatus({
//         success: false,
//         message: 'Failed to fetch billing records',
//       });
//       setTimeout(() => setEmailStatus(null), 3000);
//       return null;
//     } finally {
//       setLoadingBillingRecords(false);
//     }
//   };

//   const fetchInvoiceData = async (orderId, type = 'hotel') => {
//     setLoadingInvoice(true);
//     try {
//       let url = `${BQ_BASE_URL}/bq/api/invoice/data/${orderId}?format=folio`;

//       if (type === 'guest') {
//         if (selectedBillingType === 'room_only') {
//           url += `&room_rent=true&food_order=false&amenity_service=true&room_service=false&full_stay=false`;
//         } else if (selectedBillingType === 'full_stay') {
//           url += `&room_rent=true&food_order=true&amenity_service=true&room_service=true&full_stay=true`;
//         } else if (selectedBillingType === 'custom') {
//           url += `&room_rent=${includeCharges.room}`;
//           url += `&food_order=${includeCharges.food}`;
//           url += `&amenity_service=${includeCharges.amenities}`;
//           url += `&room_service=${includeCharges.laundry || false}`;
//           url += `&full_stay=false`;
//         }
//       }

//       const response = await fetch(url, {
//         headers: {
//           accept: 'application/json',
//         },
//       });

//       if (!response.ok) throw new Error('Failed to fetch invoice data');

//       const data = await response.json();

//       if (type === 'guest') {
//         setInvoiceData({
//           ...data,
//           adjustments: adjustments,
//           billingType: selectedBillingType,
//           note: getBillingTypeNote(selectedBillingType),
//         });
//       } else {
//         setInvoiceData(data);
//       }
//     } catch (err) {
//       console.error('Error fetching invoice:', err);
//       alert('Failed to load invoice data');
//     } finally {
//       setLoadingInvoice(false);
//     }
//   };

//   const handleSendEmailWithCustom = async (orderId, customEmailAddr) => {
//     setEmailSending(true);
//     setEmailStatus(null);

//     try {
//       let url = `${BQ_BASE_URL}/bq/api/invoice/email/${orderId}?recipient_email=${encodeURIComponent(customEmailAddr)}&format=folio`;

//       if (invoiceType === 'guest') {
//         if (selectedBillingType === 'room_only') {
//           url += `&room_rent=true&food_order=false&amenity_service=true&room_service=false&full_stay=false`;
//         } else if (selectedBillingType === 'full_stay') {
//           url += `&room_rent=true&food_order=true&amenity_service=true&room_service=true&full_stay=true`;
//         } else if (selectedBillingType === 'custom') {
//           url += `&room_rent=${includeCharges.room}`;
//           url += `&food_order=${includeCharges.food}`;
//           url += `&amenity_service=${includeCharges.amenities}`;
//           url += `&room_service=${includeCharges.laundry || false}`;
//           url += `&full_stay=false`;
//         }
//       }

//       const response = await fetch(url, {
//         method: 'POST',
//         headers: {
//           accept: 'application/json',
//         },
//       });

//       if (!response.ok) throw new Error('Failed to send email');

//       const data = await response.json();
//       setEmailStatus({
//         success: true,
//         message: data.message || `Invoice email sent successfully to ${customEmailAddr}`,
//       });

//       setTimeout(() => setEmailStatus(null), 3000);
//       setShowEmailModal(false);
//       setCustomEmail('');
//     } catch (err) {
//       console.error('Error sending email:', err);
//       setEmailStatus({
//         success: false,
//         message: err.message || 'Failed to send invoice email',
//       });
//     } finally {
//       setEmailSending(false);
//     }
//   };

//   const getBillingTypeNote = (type) => {
//     switch (type) {
//       case 'room_only':
//         return 'This invoice includes room charges only. Other services (food, laundry, etc.) are billed separately.';
//       case 'full_stay':
//         return 'This invoice includes all charges for your entire stay including room, food, and other services.';
//       case 'custom':
//         return 'This invoice includes selected charges as per your request.';
//       default:
//         return 'Thank you for staying with us.';
//     }
//   };

//   const handleGenerateInvoice = (booking) => {
//     setSelectedBooking(booking);
//     setAdjustments([]);
//     setShowBillingTypeModal(true);
//   };

//   const validateGST = (gst) => {
//     if (!gst) return '';
//     const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
//     if (!gstRegex.test(gst)) {
//       return 'Invalid GST number format (e.g., 22AAAAA0000A1Z5)';
//     }
//     return '';
//   };

//   const validateEmail = (email) => {
//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     if (!email) return 'Email is required';
//     if (!emailRegex.test(email)) return 'Please enter a valid email address';
//     return '';
//   };

//   // Helper function to format date for display
//   const formatDateForDisplay = (dateStr) => {
//     if (!dateStr) return '';
//     const date = new Date(dateStr);
//     return date.toLocaleDateString('en-IN', {
//       year: 'numeric',
//       month: 'short',
//       day: 'numeric',
//     });
//   };

//   const handleGstChange = (e) => {
//     const value = e.target.value.toUpperCase();
//     setGstNumber(value);
//     const error = validateGST(value);
//     setGstErrors(error);
//   };

//   const handleBillingTypeSelect = async () => {
//     setShowBillingTypeModal(false);
//     setInvoiceType('guest');
//     await fetchInvoiceData(selectedBooking.orderid, 'guest');
//     setShowInvoiceModal(true);
//   };

//   const handleViewHotelInvoice = async (booking) => {
//     setInvoiceType('hotel');
//     setSelectedBooking(booking);
//     await fetchInvoiceData(booking.orderid, 'hotel');
//     setShowInvoiceModal(true);
//   };

//   const handleOpenAdjustmentModal = async (booking) => {
//     setSelectedBooking(booking);
//     setCurrentAdjustment({
//       amount: '',
//       adjustment_type: 'flat',
//       is_discount: true,
//       billing_type: 'Manual Adjustment - Discount',
//       base_category: '',
//       reason: '',
//       is_taxable: false,
//       override_tax_amount: null,
//       base_amount: null,
//       billing_id: null,
//     });

//     await fetchBillingRecordsByOrder(booking.orderid);
//     setShowAdjustmentModal(true);
//   };

//   const handleAddAdjustment = async () => {
//     const amountValue = parseFloat(currentAdjustment.amount);

//     if (
//       !amountValue ||
//       amountValue <= 0 ||
//       !currentAdjustment.reason ||
//       !currentAdjustment.base_category ||
//       !selectedBooking
//     ) {
//       setEmailStatus({
//         success: false,
//         message: 'Please fill all required fields with valid values',
//       });
//       setTimeout(() => setEmailStatus(null), 3000);
//       return;
//     }

//     if (
//       currentAdjustment.adjustment_type === 'percentage' &&
//       !currentAdjustment.billing_id &&
//       !currentAdjustment.base_amount
//     ) {
//       setEmailStatus({
//         success: false,
//         message:
//           'Please select a billing record or provide a base amount for percentage calculation',
//       });
//       setTimeout(() => setEmailStatus(null), 3000);
//       return;
//     }

//     try {
//       const adjustmentPayload = {
//         bookingid: selectedBooking.bookingid,
//         amount: amountValue,
//         adjustment_type: currentAdjustment.adjustment_type,
//         is_discount: currentAdjustment.is_discount,
//         billing_type: currentAdjustment.billing_type,
//         base_category: currentAdjustment.base_category,
//         reason: currentAdjustment.reason,
//         is_taxable: currentAdjustment.is_taxable,
//         override_tax_amount: currentAdjustment.override_tax_amount,
//         created_by: staffId,
//       };

//       if (currentAdjustment.billing_id) {
//         adjustmentPayload.billing_id = currentAdjustment.billing_id;
//       }

//       if (currentAdjustment.adjustment_type === 'percentage' && currentAdjustment.base_amount) {
//         adjustmentPayload.overrde_base_amount = parseFloat(currentAdjustment.base_amount);
//       }

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/billing/manual-adjustment`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           accept: 'application/json',
//         },
//         body: JSON.stringify(adjustmentPayload),
//       });

//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(errorData.detail || 'Failed to add adjustment');
//       }

//       const result = await response.json();

//       const newAdjustment = {
//         id: result.billing_id,
//         amount: amountValue,
//         adjustment_type: currentAdjustment.adjustment_type,
//         is_discount: currentAdjustment.is_discount,
//         billing_type: currentAdjustment.billing_type,
//         base_category: currentAdjustment.base_category,
//         reason: currentAdjustment.reason,
//         billing_id: currentAdjustment.billing_id,
//         base_amount: currentAdjustment.base_amount,
//         applied_amount: result.amount_applied,
//         tax_applied: result.tax_applied,
//         final_impact: result.final_impact,
//       };

//       setAdjustments((prev) => [...prev, newAdjustment]);

//       await fetchBillingRecordsByOrder(selectedBooking.orderid);
//       await fetchAllData();

//       if (showInvoiceModal && invoiceData) {
//         await fetchInvoiceData(selectedBooking.orderid, 'guest');
//       }

//       setShowAdjustmentModal(false);

//       setCurrentAdjustment({
//         amount: '',
//         adjustment_type: 'flat',
//         is_discount: true,
//         billing_type: 'Manual Adjustment - Discount',
//         base_category: '',
//         reason: '',
//         is_taxable: true,
//         override_tax_amount: null,
//         base_amount: null,
//         billing_id: null,
//       });

//       setEmailStatus({
//         success: true,
//         message: 'Adjustment added successfully',
//       });
//       setTimeout(() => setEmailStatus(null), 3000);
//     } catch (err) {
//       console.error('Error adding adjustment:', err);
//       setEmailStatus({
//         success: false,
//         message: err.message || 'Failed to add adjustment',
//       });
//       setTimeout(() => setEmailStatus(null), 3000);
//     }
//   };

//   const handleUpdateGST = async (orderId, gstNumber) => {
//     setUpdatingGST(true);
//     setGstErrors('');

//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/update-gst/`, {
//         method: 'PATCH',
//         headers: {
//           'Content-Type': 'application/json',
//           Accept: 'application/json',
//         },
//         body: JSON.stringify({
//           order_id: orderId,
//           gst_number: gstNumber || null,
//         }),
//       });

//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(errorData.detail || 'Failed to update GST number');
//       }

//       const result = await response.json();

//       setEmailStatus({
//         success: true,
//         message: `GST number added successfully`,
//       });

//       await fetchAllData();

//       setShowGSTModal(false);
//       setGstNumber('');

//       setTimeout(() => setEmailStatus(null), 3000);
//     } catch (err) {
//       console.error('Error updating GST:', err);
//       setGstErrors(err.message || 'Failed to update GST number');
//       setEmailStatus({
//         success: false,
//         message: err.message || 'Failed to update GST number',
//       });
//       setTimeout(() => setEmailStatus(null), 3000);
//     } finally {
//       setUpdatingGST(false);
//     }
//   };

//   const handleOpenGSTModal = (booking) => {
//     setSelectedBooking(booking);
//     const existingGST = booking.gst_number || '';
//     setGstNumber(existingGST);
//     setGstErrors('');
//     setShowGSTModal(true);
//   };

//   const handleDownloadPDF = async (orderId, type) => {
//     try {
//       let url = `${BQ_BASE_URL}/bq/api/invoice/pdf/${orderId}?format=folio`;

//       if (type === 'guest') {
//         if (selectedBillingType === 'room_only') {
//           url += `&room_rent=true&food_order=false&amenity_service=true&room_service=false&full_stay=false`;
//         } else if (selectedBillingType === 'full_stay') {
//           url += `&room_rent=true&food_order=true&amenity_service=true&room_service=true&full_stay=true`;
//         } else if (selectedBillingType === 'custom') {
//           url += `&room_rent=${includeCharges.room}`;
//           url += `&food_order=${includeCharges.food}`;
//           url += `&amenity_service=${includeCharges.amenities}`;
//           url += `&room_service=${includeCharges.laundry || false}`;
//           url += `&full_stay=false`;
//         }
//       }

//       const response = await fetch(url, {
//         headers: {
//           accept: '*/*',
//         },
//       });

//       if (!response.ok) throw new Error('Failed to download PDF');

//       const blob = await response.blob();
//       const blobUrl = window.URL.createObjectURL(blob);
//       const link = document.createElement('a');
//       link.href = blobUrl;
//       link.setAttribute('download', `invoice_${orderId}_${type}.pdf`);
//       document.body.appendChild(link);
//       link.click();
//       link.parentNode.removeChild(link);
//       window.URL.revokeObjectURL(blobUrl);
//     } catch (err) {
//       console.error('Error downloading PDF:', err);
//       alert('Failed to download invoice');
//     }
//   };

//   const handleEmailInvoice = (orderId, guestEmail) => {
//     setSelectedBooking({ order_id: orderId, guest_email: guestEmail });
//     setEmailOption('guest');
//     setCustomEmail(guestEmail);
//     setEmailError('');
//     setShowEmailModal(true);
//   };

//   const handleFilterChange = (key, value) => {
//     setFilters((prev) => ({ ...prev, [key]: value }));
//     setCurrentPage(1);
//   };

//   const handlePageChange = (page, newItemsPerPage = null) => {
//     if (newItemsPerPage !== null) {
//       setItemsPerPage(newItemsPerPage);
//       setCurrentPage(1);
//     } else {
//       setCurrentPage(page);
//     }
//   };

//   const getStatusBadge = (bookingStatus, billing) => {
//     if (bookingStatus === 'Soft') {
//       return (
//         <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
//           Reserved
//         </span>
//       );
//     } else if (bookingStatus === 'Hard') {
//       if (billing?.is_fully_paid_or_over) {
//         return (
//           <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
//             Check In
//           </span>
//         );
//       } else if (billing?.total_billed > 0) {
//         return (
//           <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
//             Check In
//           </span>
//         );
//       } else {
//         return (
//           <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
//             Check In
//           </span>
//         );
//       }
//     } else if (bookingStatus === 'CheckedOut') {
//       return (
//         <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
//           Checked Out
//         </span>
//       );
//     } else if (bookingStatus === 'Cancelled') {
//       return (
//         <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
//           Cancelled
//         </span>
//       );
//     }
//     return (
//       <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
//         {bookingStatus}
//       </span>
//     );
//   };

//   // Updated summary using dashboard overview data
//   // Update the summary object (around line 500-520)
//   const summary = {
//     totalRevenue:
//       typeof dashboardOverview.total_revenue === 'number'
//         ? dashboardOverview.total_revenue
//         : parseFloat(dashboardOverview.total_revenue) || 0,
//     totalCollected:
//       typeof dashboardOverview.total_collected === 'number'
//         ? dashboardOverview.total_collected
//         : parseFloat(dashboardOverview.total_collected) || 0,
//     totalRefunded:
//       typeof dashboardOverview.total_refunded === 'number'
//         ? dashboardOverview.total_refunded
//         : parseFloat(dashboardOverview.total_refunded) || 0,
//     netOutstanding:
//       typeof dashboardOverview.net_outstanding === 'number'
//         ? dashboardOverview.net_outstanding
//         : parseFloat(dashboardOverview.net_outstanding) || 0,
//     totalAdjustments:
//       typeof dashboardOverview.total_adjustments === 'number'
//         ? dashboardOverview.total_adjustments
//         : parseFloat(dashboardOverview.total_adjustments) || 0,
//     // Add this
//     adjustmentCharges:
//       typeof dashboardOverview.adjustment_charges === 'number'
//         ? dashboardOverview.adjustment_charges
//         : parseFloat(dashboardOverview.adjustment_charges) || 0,
//     // Add this
//     adjustmentDiscounts:
//       typeof dashboardOverview.adjustment_discounts === 'number'
//         ? dashboardOverview.adjustment_discounts
//         : parseFloat(dashboardOverview.adjustment_discounts) || 0,
//     // Add this
//     totalVouchers:
//       typeof dashboardOverview.total_vouchers === 'number'
//         ? dashboardOverview.total_vouchers
//         : parseFloat(dashboardOverview.total_vouchers) || 0,
//     paidBookings:
//       typeof dashboardOverview.paid_bookings === 'number'
//         ? dashboardOverview.paid_bookings
//         : parseInt(dashboardOverview.paid_bookings) || 0,
//     totalBookings:
//       (typeof dashboardOverview.paid_bookings === 'number'
//         ? dashboardOverview.paid_bookings
//         : parseInt(dashboardOverview.paid_bookings) || 0) +
//       (typeof dashboardOverview.pending_bookings === 'number'
//         ? dashboardOverview.pending_bookings
//         : parseInt(dashboardOverview.pending_bookings) || 0),
//     pendingBookings:
//       typeof dashboardOverview.pending_bookings === 'number'
//         ? dashboardOverview.pending_bookings
//         : parseInt(dashboardOverview.pending_bookings) || 0,
//     todayCheckins:
//       typeof dashboardOverview.today_checkins === 'number'
//         ? dashboardOverview.today_checkins
//         : parseInt(dashboardOverview.today_checkins) || 0,
//     todayCheckouts:
//       typeof dashboardOverview.today_checkouts === 'number'
//         ? dashboardOverview.today_checkouts
//         : parseInt(dashboardOverview.today_checkouts) || 0,
//   };

//   const totalPages = Math.ceil(totalItems / itemsPerPage);

//   if (loading && allBookings.length === 0) {
//     return (
//       <div className="bg-white rounded-lg shadow-sm p-6">
//         <div className="flex justify-center items-center h-64">
//           <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="bg-white rounded-lg shadow-sm p-6 relative">
//       {/* Email Status Toast */}
//       {emailStatus && (
//         <div
//           className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
//             emailStatus.success
//               ? 'bg-green-50 text-green-800 border border-green-200'
//               : 'bg-red-50 text-red-800 border border-red-200'
//           }`}
//         >
//           <div className="flex items-center">
//             {emailStatus.success ? (
//               <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth={2}
//                   d="M5 13l4 4L19 7"
//                 />
//               </svg>
//             ) : (
//               <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth={2}
//                   d="M6 18L18 6M6 6l12 12"
//                 />
//               </svg>
//             )}
//             {emailStatus.message}
//           </div>
//         </div>
//       )}
//       {/* Header with Filters */}
//       <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
//         <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto flex-wrap">
//           <div className="relative">
//             <input
//               type="text"
//               placeholder="Search by Reservation No., Guest Name, Room No..."
//               className="pl-10 pr-4 py-2 border border-light-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent w-80"
//               defaultValue={filters.search}
//               onChange={(e) => debouncedSearch(e.target.value)}
//             />
//             <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
//               <svg
//                 className="w-4 h-4 text-dark-gray"
//                 fill="none"
//                 stroke="currentColor"
//                 viewBox="0 0 24 24"
//               >
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth={2}
//                   d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
//                 />
//               </svg>
//             </div>
//           </div>

//           <select
//             className="border border-light-gray rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
//             value={filters.bookingStatus}
//             onChange={(e) => handleFilterChange('bookingStatus', e.target.value)}
//           >
//             <option value="">All Booking Status</option>
//             <option value="Soft">Reserved</option>
//             <option value="Hard">Checked In</option>
//             <option value="Checkedout">Checked Out</option>
//             <option value="Cancelled">Cancelled</option>
//           </select>

//           <select
//             className="border border-light-gray rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
//             value={filters.paymentStatus}
//             onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
//           >
//             <option value="">All Payment Status</option>
//             <option value="paid">Paid</option>
//             <option value="pending">Pending</option>
//             <option value="unpaid">Unpaid</option>
//           </select>

//           {/* Checkout Date Filter Dropdown */}
//           <select
//             className="border border-light-gray rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
//             value={filters.checkoutDateFilter}
//             onChange={(e) => {
//               handleFilterChange('checkoutDateFilter', e.target.value);
//               // Clear custom date range when using preset filters
//               if (e.target.value) {
//                 setFilters((prev) => ({
//                   ...prev,
//                   dateRange: { startDate: '', endDate: '' },
//                 }));
//               }
//             }}
//           >
//             <option value="">All Checkout Dates</option>
//             <option value="today">Today</option>
//             <option value="yesterday">Yesterday</option>
//             <option value="this_week">This Week</option>
//             <option value="this_month">This Month</option>
//           </select>

//           {/* Date Range Filter */}
//           <div className="flex items-center gap-2">
//             <div className="relative">
//               <input
//                 type="date"
//                 value={filters.dateRange.startDate}
//                 onChange={(e) => {
//                   setFilters((prev) => ({
//                     ...prev,
//                     dateRange: {
//                       ...prev.dateRange,
//                       startDate: e.target.value,
//                     },
//                     // Clear preset filter when using custom date range
//                     checkoutDateFilter: '',
//                   }));
//                   setCurrentPage(1);
//                 }}
//                 className="border border-light-gray rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
//                 placeholder="Start Date"
//               />
//             </div>
//             <span className="text-dark-gray">to</span>
//             <div className="relative">
//               <input
//                 type="date"
//                 value={filters.dateRange.endDate}
//                 onChange={(e) => {
//                   setFilters((prev) => ({
//                     ...prev,
//                     dateRange: {
//                       ...prev.dateRange,
//                       endDate: e.target.value,
//                     },
//                     // Clear preset filter when using custom date range
//                     checkoutDateFilter: '',
//                   }));
//                   setCurrentPage(1);
//                 }}
//                 className="border border-light-gray rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
//                 placeholder="End Date"
//               />
//             </div>

//             {(filters.dateRange.startDate ||
//               filters.dateRange.endDate ||
//               filters.checkoutDateFilter) && (
//               <button
//                 onClick={() => {
//                   setFilters((prev) => ({
//                     ...prev,
//                     dateRange: { startDate: '', endDate: '' },
//                     checkoutDateFilter: '',
//                   }));
//                   setCurrentPage(1);
//                 }}
//                 className="p-2 text-white hover:text-gray-100 transition-colors px-4 py-2 bg-red-600 rounded-lg"
//                 title="Clear all filters"
//               >
//                 Clear All
//               </button>
//             )}
//           </div>
//         </div>
//       </div>

//       {/* Updated Summary Cards with new fields */}
//       <div className="grid grid-cols-1 md:grid-cols-5 lg:grid-cols-5 gap-4 mb-6">
//         <div className="bg-gray-50 p-4 rounded-lg border border-light-gray">
//           <h3 className="text-dark-gray text-sm font-medium mb-1">Total Revenue</h3>
//           <p className="text-black text-2xl font-bold">₹{(summary.totalRevenue || 0).toFixed(2)}</p>
//         </div>
//         <div className="bg-gray-50 p-4 rounded-lg border border-light-gray">
//           <h3 className="text-dark-gray text-sm font-medium mb-1">Total Collected</h3>
//           <p className="text-green-600 text-2xl font-bold">
//             ₹{(summary.totalCollected || 0).toFixed(2)}
//           </p>
//         </div>
//         <div className="bg-gray-50 p-4 rounded-lg border border-light-gray">
//           <h3 className="text-dark-gray text-sm font-medium mb-1">Total Refunded</h3>
//           <p className="text-red-600 text-2xl font-bold">
//             ₹{(summary.totalRefunded || 0).toFixed(2)}
//           </p>
//         </div>
//         {/* Updated Total Adjustments Card with Tooltip */}
//         <div className="bg-gray-50 p-4 rounded-lg border border-light-gray relative group">
//           <h3 className="text-dark-gray text-sm font-medium mb-1 flex items-center">
//             Total Adjustments
//             <div className="relative ml-1">
//               <svg
//                 className="w-4 h-4 text-gray-400 cursor-help"
//                 fill="none"
//                 stroke="currentColor"
//                 viewBox="0 0 24 24"
//               >
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth={2}
//                   d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
//                 />
//               </svg>
//               {/* Tooltip */}
//               <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded-lg py-2 px-3 w-48 z-10">
//                 <div className="space-y-1">
//                   <div className="flex justify-between">
//                     <span>Adjustment Charges:</span>
//                     <span className="font-semibold">₹{summary.adjustmentCharges.toFixed(2)}</span>
//                   </div>
//                   <div className="flex justify-between">
//                     <span>Adjustment Discounts:</span>
//                     <span className="font-semibold text-green-400">
//                       ₹{summary.adjustmentDiscounts.toFixed(2)}
//                     </span>
//                   </div>
//                 </div>
//                 <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-800"></div>
//               </div>
//             </div>
//           </h3>
//           <p className="text-purple-600 text-2xl font-bold">
//             ₹{summary.totalAdjustments.toFixed(2)}
//           </p>
//         </div>
//         <div className="bg-gray-50 p-4 rounded-lg border border-light-gray">
//           <h3 className="text-dark-gray text-sm font-medium mb-1">Total Vouchers:</h3>
//           <p className="text-green-600 text-2xl font-bold">{summary.totalVouchers.toFixed(2)}</p>
//         </div>
//         <div className="bg-gray-50 p-4 rounded-lg border border-light-gray">
//           <h3 className="text-dark-gray text-sm font-medium mb-1">Paid Reservations</h3>
//           <p className="text-green-600 text-2xl font-bold">
//             {summary.paidBookings || 0}/{summary.totalBookings || 0}
//           </p>
//         </div>

//         <div className="bg-gray-50 p-4 rounded-lg border border-light-gray">
//           <h3 className="text-dark-gray text-sm font-medium mb-1">Pending Reservations</h3>
//           <p className="text-orange-600 text-2xl font-bold">{summary.pendingBookings || 0}</p>
//         </div>
//         <div className="bg-gray-50 p-4 rounded-lg border border-light-gray">
//           <h3 className="text-dark-gray text-sm font-medium mb-1">Today's Check-ins</h3>
//           <p className="text-blue-600 text-2xl font-bold">{summary.todayCheckins || 0}</p>
//         </div>
//         <div className="bg-gray-50 p-4 rounded-lg border border-light-gray">
//           <h3 className="text-dark-gray text-sm font-medium mb-1">Today's Check-outs</h3>
//           <p className="text-indigo-600 text-2xl font-bold">{summary.todayCheckouts || 0}</p>
//         </div>
//       </div>

//       {/* Bookings Table - Same as before but with adjusted data structure */}
//       <div className="overflow-x-auto">
//         <table className="w-full">
//           <thead>
//             <tr className="border-b border-light-gray">
//               <th className="text-left py-3 px-2 font-semibold text-black">Reservation No.</th>
//               <th className="text-left py-3 px-2 font-semibold text-black">Guest Name</th>
//               <th className="text-left py-3 px-2 font-semibold text-black">Room</th>
//               <th className="text-left py-3 px-2 font-semibold text-black">Check In/Out</th>
//               <th className="text-left py-3 px-2 font-semibold text-black">Status</th>
//               <th className="text-left py-3 px-2 font-semibold text-black">Total</th>
//               <th className="text-left py-3 px-2 font-semibold text-black">Paid</th>
//               <th className="text-left py-3 px-2 font-semibold text-black">Due</th>
//               <th className="text-left py-3 px-2 font-semibold text-black">GST</th>
//               <th className="text-left py-3 px-2 font-semibold text-black">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {displayedBookings.map((booking, index) => (
//               <tr
//                 key={booking.bookingid}
//                 className={`border-b border-light-gray ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
//               >
//                 <td className="py-3 px-4 text-dark-gray">{booking.orderid}</td>
//                 <td className="py-3 px-4 text-black">
//                   {booking.firstname} {booking.lastname}
//                   <div className="text-xs text-dark-gray">{booking.emailid}</div>
//                 </td>
//                 <td className="py-3 px-2">
//                   <div className="text-black">Room {booking.roomnumber || 'N/A'}</div>
//                   <div className="text-xs text-dark-gray">{booking.roomtypename}</div>
//                 </td>
//                 <td className="py-3 px-2 text-dark-gray">
//                   <div>{booking.checkindate}</div>
//                   <span className="font-bold">to</span>
//                   <div> {booking.checkoutdate}</div>
//                 </td>
//                 <td className="py-3 px-2">
//                   {getStatusBadge(booking.booking_status, booking.billing)}
//                 </td>
//                 <td className="py-3 px-2 text-black font-medium">
//                   ₹{booking.billing?.total_billed?.toFixed(2) || '0.00'}
//                 </td>
//                 <td className="py-3 px-2 text-green-600 font-medium">
//                   ₹{booking.billing?.total_paid?.toFixed(2) || '0.00'}
//                 </td>
//                 <td className="py-3 px-2">
//                   {booking.billing?.net_payable > 0 ? (
//                     <span className="text-red-600 font-medium">
//                       ₹{booking.billing.net_payable.toFixed(2)}
//                     </span>
//                   ) : (
//                     <span className="text-green-600">-</span>
//                   )}
//                 </td>
//                 <td className="py-3 px-2">
//                   {booking.gst_number && booking.gst_number !== '' ? (
//                     <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded border border-gray-200">
//                       {booking.gst_number}
//                     </span>
//                   ) : (
//                     <span className="text-gray-400 text-xs">Not added</span>
//                   )}
//                 </td>
//                 <td className="py-3 px-2">
//                   {booking.booking_status !== 'Soft' ? (
//                     <div className="flex space-x-2">
//                       <button
//                         onClick={() =>
//                           navigate('/dashboard/core-modules/finance/adjustment-details', {
//                             state: { booking },
//                           })
//                         }
//                         className="text-orange-600 hover:text-orange-800 transition-colors"
//                         title="View Adjustment Details"
//                       >
//                         <svg
//                           className="w-5 h-5"
//                           fill="none"
//                           stroke="currentColor"
//                           viewBox="0 0 24 24"
//                         >
//                           <path
//                             strokeLinecap="round"
//                             strokeLinejoin="round"
//                             strokeWidth={2}
//                             d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
//                           />
//                         </svg>
//                       </button>

//                       <button
//                         onClick={() => handleViewHotelInvoice(booking)}
//                         className="text-blue-600 hover:text-blue-800 transition-colors"
//                         title="View Hotel Invoice (Full Details)"
//                       >
//                         <svg
//                           className="w-5 h-5"
//                           fill="none"
//                           stroke="currentColor"
//                           viewBox="0 0 24 24"
//                         >
//                           <path
//                             strokeLinecap="round"
//                             strokeLinejoin="round"
//                             strokeWidth={2}
//                             d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
//                           />
//                         </svg>
//                       </button>

//                       <button
//                         onClick={() => handleGenerateInvoice(booking)}
//                         className="text-green-600 hover:text-green-800 transition-colors"
//                         title="Generate Guest Invoice with Adjustments"
//                       >
//                         <svg
//                           className="w-5 h-5"
//                           fill="none"
//                           stroke="currentColor"
//                           viewBox="0 0 24 24"
//                         >
//                           <path
//                             strokeLinecap="round"
//                             strokeLinejoin="round"
//                             strokeWidth={2}
//                             d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
//                           />
//                         </svg>
//                       </button>

//                       <button
//                         onClick={() => handleDownloadPDF(booking.orderid, 'hotel')}
//                         className="text-gray-600 hover:text-gray-800 transition-colors"
//                         title="Download PDF"
//                       >
//                         <svg
//                           className="w-5 h-5"
//                           fill="none"
//                           stroke="currentColor"
//                           viewBox="0 0 24 24"
//                         >
//                           <path
//                             strokeLinecap="round"
//                             strokeLinejoin="round"
//                             strokeWidth={2}
//                             d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
//                           />
//                         </svg>
//                       </button>
//                       <button
//                         onClick={() => handleOpenGSTModal(booking)}
//                         className="text-indigo-600 hover:text-indigo-800 transition-colors"
//                         title="Add/Update GST Number"
//                       >
//                         <svg
//                           className="w-5 h-5"
//                           fill="none"
//                           stroke="currentColor"
//                           viewBox="0 0 24 24"
//                         >
//                           <circle cx="12" cy="12" r="12" strokeWidth={1.5} fill="none" />
//                           <text
//                             x="12"
//                             y="17"
//                             textAnchor="middle"
//                             fill="currentColor"
//                             fontSize="12"
//                             fontWeight="semibold"
//                           >
//                             GST
//                           </text>
//                         </svg>
//                       </button>
//                     </div>
//                   ) : (
//                     <span className="text-gray-400 text-xs">No actions</span>
//                   )}
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>

//         {displayedBookings.length === 0 && !loading && (
//           <div className="text-center py-8 text-dark-gray">
//             No bookings found matching your criteria
//           </div>
//         )}
//       </div>

//       {totalPages > 0 && (
//         <Pagination
//           currentPage={currentPage}
//           totalPages={totalPages}
//           totalItems={totalItems}
//           itemsPerPage={itemsPerPage}
//           onPageChange={handlePageChange}
//         />
//       )}

//       {/* All modals remain the same */}
//       {/* Billing Type Selection Modal */}
//       {showBillingTypeModal && selectedBooking && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
//             <h3 className="text-xl font-bold text-black mb-4">Invoice Assistant</h3>
//             <p className="text-dark-gray mb-4">
//               Guest Name: {selectedBooking.firstname} {selectedBooking.lastname}
//               <br />
//               Reservation Number: {selectedBooking.orderid}
//             </p>

//             <div className="space-y-4">
//               <div className="flex flex-col md:flex-row md:space-x-4">
//                 <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 flex-1">
//                   <input
//                     type="radio"
//                     name="billingType"
//                     value="full_stay"
//                     checked={selectedBillingType === 'full_stay'}
//                     onChange={(e) => setSelectedBillingType(e.target.value)}
//                     className="w-5 h-5 mr-3 accent-black"
//                   />
//                   <div>
//                     <span className="font-medium text-black">Guest Invoice</span>
//                     <p className="text-sm text-dark-gray">
//                       Complete invoice including all charges (room, food, amenities, services)
//                     </p>
//                   </div>
//                 </label>

//                 <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 flex-1">
//                   <input
//                     type="radio"
//                     name="billingType"
//                     value="custom"
//                     checked={selectedBillingType === 'custom'}
//                     onChange={(e) => setSelectedBillingType(e.target.value)}
//                     className="w-5 h-5 mr-3 accent-black"
//                   />
//                   <div>
//                     <span className="font-medium text-black">Custom Invoice</span>
//                     <p className="text-sm text-dark-gray">
//                       Select specific charges to include in the invoice
//                     </p>
//                   </div>
//                 </label>
//               </div>

//               {selectedBillingType === 'custom' && (
//                 <div className="border rounded-lg p-4 space-y-1 text-sm">
//                   <div className="flex items-center justify-between mb-2 border-b pb-1">
//                     <span className="font-medium text-black">Select Charges to Include</span>
//                     <label className="relative inline-flex items-center cursor-pointer">
//                       <input
//                         type="checkbox"
//                         className="sr-only peer"
//                         checked={Object.values(includeCharges).every((v) => v)}
//                         onChange={(e) => {
//                           const checked = e.target.checked;
//                           setIncludeCharges({
//                             room: checked,
//                             food: checked,
//                             laundry: checked,
//                             amenities: checked,
//                             other: checked,
//                           });
//                         }}
//                       />
//                       <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
//                     </label>
//                   </div>

//                   <label className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
//                     <input
//                       type="checkbox"
//                       checked={includeCharges.room}
//                       onChange={(e) =>
//                         setIncludeCharges({
//                           ...includeCharges,
//                           room: e.target.checked,
//                         })
//                       }
//                       className="w-4 h-4 mr-3 accent-black"
//                     />
//                     <span className="text-black">Room Charges</span>
//                   </label>
//                   <label className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
//                     <input
//                       type="checkbox"
//                       checked={includeCharges.food}
//                       onChange={(e) =>
//                         setIncludeCharges({
//                           ...includeCharges,
//                           food: e.target.checked,
//                         })
//                       }
//                       className="w-4 h-4 mr-3 accent-black"
//                     />
//                     <span className="text-black">Food & Beverages</span>
//                   </label>
//                   <label className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
//                     <input
//                       type="checkbox"
//                       checked={includeCharges.laundry}
//                       onChange={(e) =>
//                         setIncludeCharges({
//                           ...includeCharges,
//                           laundry: e.target.checked,
//                         })
//                       }
//                       className="w-4 h-4 mr-3 accent-black"
//                     />
//                     <span className="text-black">Laundry</span>
//                   </label>
//                   <label className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
//                     <input
//                       type="checkbox"
//                       checked={includeCharges.amenities}
//                       onChange={(e) =>
//                         setIncludeCharges({
//                           ...includeCharges,
//                           amenities: e.target.checked,
//                         })
//                       }
//                       className="w-4 h-4 mr-3 accent-black"
//                     />
//                     <span className="text-black">Amenities</span>
//                   </label>
//                 </div>
//               )}

//               <div className="border-t pt-1">
//                 <label className="flex items-center cursor-pointer">
//                   <input
//                     type="checkbox"
//                     checked={showDiscount}
//                     onChange={(e) => setShowDiscount(e.target.checked)}
//                     className="w-4 h-4 mr-3 accent-black"
//                   />
//                   <span className="text-black">Show existing adjustments on invoice</span>
//                 </label>
//               </div>
//             </div>

//             <div className="flex justify-end space-x-3 mt-6">
//               <button
//                 onClick={() => setShowBillingTypeModal(false)}
//                 className="px-4 py-2 border border-light-gray rounded-lg text-dark-gray hover:bg-gray-50"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleBillingTypeSelect}
//                 className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
//               >
//                 Generate Invoice
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* GST Number Update Modal */}
//       {showGSTModal && selectedBooking && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg p-6 max-w-md w-full">
//             <div className="flex justify-between items-start mb-4">
//               <h3 className="text-xl font-bold text-black">Update GST Number</h3>
//               <button
//                 onClick={() => {
//                   setShowGSTModal(false);
//                   setGstNumber('');
//                   setGstErrors('');
//                 }}
//                 className="text-dark-gray hover:text-black"
//               >
//                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                   <path
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                     strokeWidth={2}
//                     d="M6 18L18 6M6 6l12 12"
//                   />
//                 </svg>
//               </button>
//             </div>

//             <div className="mb-4">
//               <p className="text-dark-gray text-sm mb-2">
//                 <span className="font-medium">Reservation Number:</span> {selectedBooking.orderid}
//               </p>
//               <p className="text-dark-gray text-sm mb-2">
//                 <span className="font-medium">Guest Name:</span> {selectedBooking.firstname}{' '}
//                 {selectedBooking.lastname}
//               </p>
//               {selectedBooking.gst_number && (
//                 <p className="text-dark-gray text-sm mb-4">
//                   <span className="font-medium">Current GST:</span> {selectedBooking.gst_number}
//                 </p>
//               )}
//             </div>

//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 GST Number <span className="text-gray-400 text-xs">(optional)</span>
//               </label>
//               <input
//                 type="text"
//                 value={gstNumber}
//                 onChange={handleGstChange}
//                 placeholder="Enter GST number (e.g., 22AAAAA0000A1Z5)"
//                 className="w-full p-2 border border-gray-300 rounded-md text-sm uppercase focus:outline-none focus:ring-2 focus:ring-black"
//               />
//               {gstErrors && <p className="text-xs text-red-500 mt-1">{gstErrors}</p>}
//             </div>

//             <div className="flex justify-end space-x-3 mt-6">
//               <button
//                 onClick={() => {
//                   setShowGSTModal(false);
//                   setGstNumber('');
//                   setGstErrors('');
//                 }}
//                 className="px-4 py-2 border border-light-gray rounded-lg text-dark-gray hover:bg-gray-50"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={() => handleUpdateGST(selectedBooking.orderid, gstNumber || null)}
//                 disabled={updatingGST || (gstNumber && gstErrors)}
//                 className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
//               >
//                 {updatingGST ? (
//                   <>
//                     <svg
//                       className="w-4 h-4 mr-2 animate-spin"
//                       fill="none"
//                       stroke="currentColor"
//                       viewBox="0 0 24 24"
//                     >
//                       <path
//                         strokeLinecap="round"
//                         strokeLinejoin="round"
//                         strokeWidth={2}
//                         d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
//                       />
//                     </svg>
//                     Updating...
//                   </>
//                 ) : (
//                   'Update GST Number'
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Email Selection Modal */}
//       {showEmailModal && selectedBooking && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg p-6 max-w-md w-full">
//             <div className="flex justify-between items-start mb-4">
//               <h3 className="text-xl font-bold text-black">Send Invoice Email</h3>
//               <button
//                 onClick={() => {
//                   setShowEmailModal(false);
//                   setCustomEmail('');
//                   setEmailError('');
//                 }}
//                 className="text-dark-gray hover:text-black"
//               >
//                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                   <path
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                     strokeWidth={2}
//                     d="M6 18L18 6M6 6l12 12"
//                   />
//                 </svg>
//               </button>
//             </div>

//             <div className="mb-4">
//               <p className="text-dark-gray text-sm mb-2">
//                 <span className="font-medium">Reservation Number:</span> {selectedBooking.order_id}
//               </p>
//               <p className="text-dark-gray text-sm mb-4">
//                 <span className="font-medium">Invoice Type:</span> Guest Invoice
//               </p>
//             </div>

//             <div className="space-y-3">
//               <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
//                 <input
//                   type="radio"
//                   name="emailOption"
//                   value="guest"
//                   checked={emailOption === 'guest'}
//                   onChange={() => {
//                     setEmailOption('guest');
//                     setCustomEmail(selectedBooking.guest_email);
//                     setEmailError('');
//                   }}
//                   className="w-5 h-5 mt-0.5 mr-3 accent-black"
//                 />
//                 <div className="flex-1">
//                   <span className="font-medium text-black">Send to Guest's Email</span>
//                   <p className="text-sm text-dark-gray">{selectedBooking.guest_email}</p>
//                 </div>
//               </label>

//               <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
//                 <input
//                   type="radio"
//                   name="emailOption"
//                   value="custom"
//                   checked={emailOption === 'custom'}
//                   onChange={() => {
//                     setEmailOption('custom');
//                     setCustomEmail('');
//                     setEmailError('');
//                   }}
//                   className="w-5 h-5 mt-0.5 mr-3 accent-black"
//                 />
//                 <div className="flex-1">
//                   <span className="font-medium text-black">Send to Different Email</span>
//                   <p className="text-sm text-dark-gray">Enter email address below</p>
//                 </div>
//               </label>

//               {emailOption === 'custom' && (
//                 <div className="mt-2 pl-8">
//                   <input
//                     type="email"
//                     value={customEmail}
//                     onChange={(e) => {
//                       setCustomEmail(e.target.value);
//                       setEmailError(validateEmail(e.target.value));
//                     }}
//                     placeholder="Enter email address"
//                     className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black"
//                   />
//                   {emailError && <p className="text-xs text-red-500 mt-1">{emailError}</p>}
//                 </div>
//               )}
//             </div>

//             <div className="flex justify-end space-x-3 mt-6">
//               <button
//                 onClick={() => {
//                   setShowEmailModal(false);
//                   setCustomEmail('');
//                   setEmailError('');
//                 }}
//                 className="px-4 py-2 border border-light-gray rounded-lg text-dark-gray hover:bg-gray-50"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={() => {
//                   if (emailOption === 'custom' && !customEmail) {
//                     setEmailError('Please enter an email address');
//                     return;
//                   }
//                   if (emailOption === 'custom' && validateEmail(customEmail)) {
//                     setEmailError(validateEmail(customEmail));
//                     return;
//                   }
//                   const recipientEmail =
//                     emailOption === 'guest' ? selectedBooking.guest_email : customEmail;
//                   handleSendEmailWithCustom(selectedBooking.order_id, recipientEmail);
//                 }}
//                 disabled={
//                   emailSending || (emailOption === 'custom' && (!customEmail || emailError))
//                 }
//                 className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
//               >
//                 {emailSending ? (
//                   <>
//                     <svg
//                       className="w-4 h-4 mr-2 animate-spin"
//                       fill="none"
//                       stroke="currentColor"
//                       viewBox="0 0 24 24"
//                     >
//                       <path
//                         strokeLinecap="round"
//                         strokeLinejoin="round"
//                         strokeWidth={2}
//                         d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
//                       />
//                     </svg>
//                     Sending...
//                   </>
//                 ) : (
//                   'Send Invoice'
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Invoice Modal */}
//       {showInvoiceModal && invoiceData && (
//         <InvoiceModal
//           invoiceData={invoiceData}
//           invoiceType={invoiceType}
//           onClose={() => {
//             setShowInvoiceModal(false);
//             setInvoiceData(null);
//           }}
//           onDownload={() => handleDownloadPDF(invoiceData.order_id, invoiceType)}
//           onEmail={() => handleEmailInvoice(invoiceData.order_id, invoiceData.guest_email)}
//           adjustments={adjustments}
//           loading={loadingInvoice}
//           emailSending={emailSending}
//         />
//       )}
//     </div>
//   );
// }

// // Invoice Modal Component (unchanged)
// function InvoiceModal({
//   invoiceData,
//   invoiceType,
//   onClose,
//   onDownload,
//   onEmail,
//   adjustments,
//   loading,
//   emailSending,
// }) {
//   const isHotelInvoice = invoiceType === 'hotel';
//   const [showAdjustments, setShowAdjustments] = useState(true);

//   const displayLines = isHotelInvoice
//     ? invoiceData.folio_lines
//     : invoiceData.filtered_folio_lines || invoiceData.folio_lines || [];

//   const formatDate = (dateStr) => {
//     if (!dateStr) return '';
//     if (dateStr.includes('/')) return dateStr;
//     return new Date(dateStr).toLocaleDateString();
//   };

//   if (loading) {
//     return (
//       <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//         <div className="bg-white rounded-lg p-8">
//           <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
//           <p className="text-center mt-4">Loading invoice...</p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
//       <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
//         <div className="flex justify-between items-start mb-6">
//           <div>
//             <div className="flex items-center space-x-3">
//               <h2 className="text-2xl font-bold text-black">
//                 {isHotelInvoice ? 'Hotel Invoice' : 'Guest Invoice'}
//               </h2>
//               {!isHotelInvoice && invoiceData.billingType && (
//                 <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded capitalize">
//                   {invoiceData.billingType.replace('_', ' ')}
//                 </span>
//               )}
//             </div>
//             <p className="text-dark-gray mt-1">Invoice #: {invoiceData.invoice_number}</p>
//             <p className="text-dark-gray text-sm">Reservation No. #: {invoiceData.order_id}</p>
//             <p className="text-dark-gray text-sm">
//               Issued: {new Date(invoiceData.invoice_date).toLocaleString()}
//             </p>
//             {invoiceData.bill_prepared_in && (
//               <p className="text-dark-gray text-sm">
//                 Bill Prepared In: {invoiceData.bill_prepared_in}
//               </p>
//             )}
//           </div>
//           <button onClick={onClose} className="text-dark-gray hover:text-black">
//             <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//               <path
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 strokeWidth={2}
//                 d="M6 18L18 6M6 6l12 12"
//               />
//             </svg>
//           </button>
//         </div>

//         <div className="grid grid-cols-2 gap-6 mb-6">
//           <div className="p-4 bg-gray-50 rounded-lg">
//             <h3 className="font-semibold text-black mb-2">{invoiceData.property_name}</h3>
//             <p className="text-dark-gray text-sm">{invoiceData.property_address}</p>
//             <p className="text-dark-gray text-sm">Contact: {invoiceData.property_contact}</p>
//             <p className="text-dark-gray text-sm">Email: {invoiceData.property_email}</p>
//             {invoiceData.property_gst && (
//               <p className="text-dark-gray text-sm">GST: {invoiceData.property_gst}</p>
//             )}
//           </div>

//           <div className="p-4 bg-gray-50 rounded-lg">
//             <h3 className="font-semibold text-black mb-2">Guest Details</h3>
//             <p className="text-black font-medium">{invoiceData.guest_name}</p>
//             <p className="text-dark-gray text-sm">{invoiceData.guest_email}</p>
//             <p className="text-dark-gray text-sm">{invoiceData.guest_phone}</p>
//             {invoiceData.company_name && (
//               <p className="text-dark-gray text-sm">Company: {invoiceData.company_name}</p>
//             )}
//             {invoiceData.company_gst && (
//               <p className="text-dark-gray text-sm">Company GST: {invoiceData.company_gst}</p>
//             )}
//           </div>
//         </div>

//         <div className="grid grid-cols-4 gap-4 mb-6">
//           <div className="p-3 border rounded-lg">
//             <p className="text-xs text-dark-gray">Room</p>
//             <p className="text-black font-medium">
//               {invoiceData.room_number} • {invoiceData.room_type}
//             </p>
//           </div>
//           <div className="p-3 border rounded-lg">
//             <p className="text-xs text-dark-gray">Check In</p>
//             <p className="text-black">{invoiceData.check_in_date}</p>
//           </div>
//           <div className="p-3 border rounded-lg">
//             <p className="text-xs text-dark-gray">Check Out</p>
//             <p className="text-black">{invoiceData.check_out_date}</p>
//           </div>
//           <div className="p-3 border rounded-lg">
//             <p className="text-xs text-dark-gray">Pax</p>
//             <p className="text-black">
//               {invoiceData.total_pax} ({invoiceData.pax_label})
//             </p>
//           </div>
//         </div>

//         {!isHotelInvoice && invoiceData.note && (
//           <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
//             <p className="text-yellow-800 text-sm flex items-center">
//               <svg
//                 className="w-5 h-5 mr-2 flex-shrink-0"
//                 fill="none"
//                 stroke="currentColor"
//                 viewBox="0 0 24 24"
//               >
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth={2}
//                   d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
//                 />
//               </svg>
//               {invoiceData.note}
//             </p>
//           </div>
//         )}

//         <div className="border-t border-light-gray pt-4">
//           <h3 className="font-semibold text-black mb-3">Charge Details</h3>
//           <div className="overflow-x-auto">
//             <table className="w-full">
//               <thead>
//                 <tr className="border-b border-light-gray bg-gray-50">
//                   <th className="text-left py-2 px-3 font-semibold text-black text-sm">Date</th>
//                   <th className="text-left py-2 px-3 font-semibold text-black text-sm">HSN/SAC</th>
//                   <th className="text-left py-2 px-3 font-semibold text-black text-sm">
//                     Description
//                   </th>
//                   <th className="text-left py-2 px-3 font-semibold text-black text-sm">Mode</th>
//                   <th className="text-left py-2 px-3 font-semibold text-black text-sm">Voucher</th>
//                   <th className="text-right py-2 px-3 font-semibold text-black text-sm">
//                     Charges (₹)
//                   </th>
//                   <th className="text-right py-2 px-3 font-semibold text-black text-sm">
//                     Credit (₹)
//                   </th>
//                   <th className="text-right py-2 px-3 font-semibold text-black text-sm">
//                     Balance (₹)
//                   </th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {displayLines.map((line, index) => (
//                   <tr
//                     key={index}
//                     className={`border-b border-light-gray ${
//                       line.description.includes('Day Total') || line.description.includes('C/F')
//                         ? 'bg-gray-100 font-medium'
//                         : ''
//                     }`}
//                   >
//                     <td className="py-2 px-3 text-dark-gray text-sm">{formatDate(line.date)}</td>
//                     <td className="py-2 px-3 text-dark-gray text-sm">{line.hsn_sac || '-'}</td>
//                     <td className="py-2 px-3 text-black text-sm">{line.description}</td>
//                     <td className="py-2 px-3 text-dark-gray text-sm">{line.mode || '-'}</td>
//                     <td className="py-2 px-3 text-dark-gray text-sm">{line.voucher || '-'}</td>
//                     <td className="py-2 px-3 text-right text-black text-sm">
//                       {line.charges && parseFloat(line.charges) > 0
//                         ? `₹${parseFloat(line.charges).toFixed(2)}`
//                         : '-'}
//                     </td>
//                     <td className="py-2 px-3 text-right text-dark-gray text-sm">
//                       {line.credit && parseFloat(line.credit) > 0
//                         ? `₹${parseFloat(line.credit).toFixed(2)}`
//                         : '-'}
//                     </td>
//                     <td className="py-2 px-3 text-right text-black text-sm font-medium">
//                       ₹{parseFloat(line.balance || 0).toFixed(2)}
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         </div>

//         {!isHotelInvoice && adjustments.length > 0 && (
//           <div className="mt-4 border-t border-light-gray pt-4">
//             <div className="flex items-center justify-between mb-3">
//               <h3 className="font-semibold text-black">Manual Adjustments</h3>
//               <label className="flex items-center cursor-pointer">
//                 <span className="text-sm text-dark-gray mr-2">Show on Invoice</span>
//                 <div className="relative">
//                   <input
//                     type="checkbox"
//                     className="sr-only"
//                     checked={showAdjustments}
//                     onChange={(e) => setShowAdjustments(e.target.checked)}
//                   />
//                   <div
//                     className={`block w-10 h-6 rounded-full transition-colors ${showAdjustments ? 'bg-purple-600' : 'bg-gray-300'}`}
//                   ></div>
//                   <div
//                     className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${showAdjustments ? 'transform translate-x-4' : ''}`}
//                   ></div>
//                 </div>
//               </label>
//             </div>

//             {showAdjustments && (
//               <div className="space-y-2">
//                 {adjustments.map((adj) => (
//                   <div
//                     key={adj.id}
//                     className="flex justify-between items-center p-2 bg-gray-50 rounded-lg"
//                   >
//                     <div className="flex-1">
//                       <div className="flex items-center">
//                         <span
//                           className={`text-sm font-medium ${adj.is_discount ? 'text-green-600' : 'text-red-600'}`}
//                         >
//                           {adj.is_discount ? 'Discount' : 'Extra Charge'}
//                           {adj.adjustment_type === 'percentage' ? ` (${adj.amount}%)` : ''}
//                         </span>
//                         <span className="text-xs text-dark-gray ml-2">({adj.reason})</span>
//                       </div>
//                       {adj.base_category && (
//                         <p className="text-xs text-dark-gray">Category: {adj.base_category}</p>
//                       )}
//                       {adj.tax_applied > 0 && (
//                         <p className="text-xs text-dark-gray">Tax: ₹{adj.tax_applied.toFixed(2)}</p>
//                       )}
//                     </div>
//                     <div className="flex items-center">
//                       <span
//                         className={`font-medium ${adj.is_discount ? 'text-green-600' : 'text-red-600'}`}
//                       >
//                         {adj.is_discount ? '-' : '+'}₹
//                         {Math.abs(adj.final_impact || adj.amount).toFixed(2)}
//                       </span>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </div>
//         )}

//         <div className="mt-6 flex justify-end">
//           <div className="w-80">
//             {invoiceData.summary &&
//               invoiceData.summary.map((item, index) => (
//                 <div
//                   key={index}
//                   className="flex justify-between py-2 border-t border-light-gray first:border-t-0"
//                 >
//                   <span className="text-dark-gray">{item.label}</span>
//                   <span className="text-black font-medium">
//                     ₹{parseFloat(item.amount).toFixed(2)}
//                   </span>
//                 </div>
//               ))}

//             {!isHotelInvoice && adjustments && adjustments.length > 0 && (
//               <div className="border-t border-dashed border-light-gray pt-2 mt-2">
//                 <div className="text-xs text-dark-gray mb-1">Manual Adjustments Applied:</div>
//                 {adjustments.map((adj) => (
//                   <div key={adj.id} className="flex justify-between py-1 text-sm">
//                     <span className="text-dark-gray">{adj.reason}</span>
//                     <span className={adj.is_discount ? 'text-green-600' : 'text-red-600'}>
//                       {adj.is_discount ? '-' : '+'}₹{Math.abs(adj.amount).toFixed(2)}
//                     </span>
//                   </div>
//                 ))}
//               </div>
//             )}

//             <div className="flex justify-between py-2 mt-1 border-t-2 border-black font-bold">
//               <span className="text-black text-lg">Total Amount</span>
//               <span className="text-black text-lg">
//                 ₹{parseFloat(invoiceData.grand_total || 0).toFixed(2)}
//               </span>
//             </div>
//           </div>
//         </div>

//         {invoiceData.declaration && (
//           <div className="mt-6 pt-4 border-t border-b pb-4 border-light-gray">
//             <p className="text-center text-dark-gray text-xs whitespace-pre-line">
//               {invoiceData.declaration}
//             </p>
//           </div>
//         )}

//         <div className="mt-6 flex justify-end space-x-3">
//           <button
//             onClick={onClose}
//             className="px-4 py-2 border border-light-gray rounded-lg text-dark-gray hover:bg-gray-50"
//           >
//             Close
//           </button>
//           {!isHotelInvoice && (
//             <button
//               onClick={onEmail}
//               disabled={emailSending}
//               className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center disabled:opacity-50"
//             >
//               {emailSending ? (
//                 <>
//                   <svg
//                     className="w-4 h-4 mr-2 animate-spin"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                   >
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
//                     />
//                   </svg>
//                   Sending...
//                 </>
//               ) : (
//                 <>
//                   <svg
//                     className="w-4 h-4 mr-2"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                   >
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
//                     />
//                   </svg>
//                   Email Invoice
//                 </>
//               )}
//             </button>
//           )}
//           <button
//             onClick={onDownload}
//             className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 flex items-center"
//           >
//             <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//               <path
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 strokeWidth={2}
//                 d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
//               />
//             </svg>
//             Download PDF
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default FinanceAndAccounting;
